/**
 * setupState.ts — the one derivation the whole setup page is drawn from.
 *
 * THE MANIFEST IS THE ACTIVATION PREFLIGHT. The rows an admin reads down the left, the sentence
 * the narrator speaks, the three progress numbers and the `Activate contract` gate are all
 * produced HERE, from one snapshot, so they cannot disagree with each other. The screen this
 * replaces derived completion in four places — the rail, the review board, the step headers and
 * the server — and the four gave different answers; the loudest symptom was a green button the
 * server then refused.
 *
 * ── The 22 rows ──────────────────────────────────────────────────────────────────────────────
 *
 * Twenty-two rows, three classes, three voices (`tasks/setup-redesign/ORGANISATION.md`):
 *
 *   gate    (8) "required to activate"                       — `sign_contract` refuses without it
 *   render  (8) "the agreement cannot be sent without this"  — a required merge field of the
 *                                                              hire purchase or witness template
 *   record  (6) "on file / not on file"                      — never urgency, never overdue
 *
 * A RECORD IS NOT WORK ANYBODY OWES, WHICH IS NOT THE SAME AS NOT BEING THE ADMIN'S TO DO. It stays
 * settled, it stays out of the gate count and it can never look overdue — but where it is empty and
 * the admin is the one who would fill it in, the manifest draws it as their move (`markFor`). TWO
 * CONTRACT SHAPES KEEP THE DORMANT GREY, AND ONLY TWO: the contract that took no deposit at all,
 * where both deposit rows go quiet because the server has stopped asking for either of them
 * [D-B41]; and the contract on one of Primelane's own vehicles, where the Vehicle Schedule row goes
 * quiet because there is no third party to sign one [D-B48]. Marks and counters read the flag; no
 * copy prints it.
 *
 * FOUR ROWS CHANGE CLASS WITH THE CONTRACT rather than with the row: the insurance-loan agreement
 * is a gate wherever the contract carries a loan [D-B14], the deposit agreement wherever it carries
 * a deposit [D-B15], the deposit PAYMENT falls from render to record wherever it carries none
 * [D-B41], and the VEHICLE SCHEDULE is a gate wherever the vehicle belongs to a third-party partner
 * [D-B48]. So a contract with a loan, a deposit and a partner's vehicle draws eleven gates, eight
 * renders and three records; one with a loan, no deposit and one of Primelane's own cars draws nine
 * gates, seven renders and six records, three of them dormant. The word "optional" renders on none
 * of them in any state (owner's ruling): the loan's paper is owed from the first draw and names the
 * order its chain is done in, and the deposit's two rows say nothing at all rather than advertising
 * themselves as skippable.
 *
 * THE CLASS IS A PROMISE ABOUT THE SERVER, NOT A DECORATION, so each one is keyed to a verified
 * predicate rather than to a feeling about importance:
 *
 * - the eight standing gates are exactly what `sign_contract` reads — the six
 *   `get_preparation_status` items (five vehicle, one insurance) plus the two legacy
 *   `contract_agreements` rows — and the three conditional ones are what it reads through
 *   `get_contract_setup_status` since `20260804010000`, the vehicle schedule among them since
 *   `20260908010000` [D-B48];
 * - the eight render-blockers are the required-field lists of the two templates
 *   (`20260801010000:2168` for the hire purchase agreement, `:2254` for the witness) — the cash
 *   price is the eighth, and the strictest of them: `contract.cash_price` is a required merge
 *   field of the hire purchase schedule AND the board refuses to GENERATE the instrument at all
 *   while the column is null (`boardSlots.cashPriceBlockFor`), so this one blocks a step earlier
 *   than the phrase "cannot be sent" promises;
 * - everything else is a record. Sixteen further items are canvas records with no row at all.
 *
 * Two consequences of reading the server rather than the prose are worth naming, because both
 * were classed differently in the IA prose and the prototype:
 *
 * - THE DEPOSIT PAYMENT IS A RENDER-BLOCKER WHEREVER A PAYMENT IS LINKED, AND A RECORD WHERE NONE
 *   IS. `contract.deposit_amount` is a required merge field of the hire purchase template, and
 *   `agreement_merge_data` resolves it to '0.00' on a contract carrying NO deposit row and to NULL
 *   on one whose rows sum to zero [D-B41]. The linked zero is therefore the case that blocks the
 *   send, and the only one: an undeposited contract renders, sends and activates, so its row goes
 *   quiet rather than naming a blocker the server does not hold.
 * - THE VEHICLE ASSIGNMENT AND THE WEEKLY INSTALMENT ARE RENDER-BLOCKERS, not gates.
 *   `sign_contract` reads neither `contracts.vehicle_id` nor `contract_instalments`; both are
 *   required merge fields. Calling them gates would put a number in the footer that the server
 *   does not hold, which is the exact defect this page exists to end.
 *
 * ── The four states ──────────────────────────────────────────────────────────────────────────
 *
 * This is a wait-on-other-humans journey — roughly twenty minutes of typing spread across a week
 * of waiting — so "incomplete" is not one state:
 *
 *   your-move  the admin can finish it now
 *   waiting    somebody else holds it (with who, and for how long)
 *   locked     it cannot start yet (with the unlock condition, in words)
 *   done       settled, with its dated receipt
 *
 * plus a `draft` modifier that never counts as progress. Record rows are `done` whether or not
 * the thing is on file — `meta.recorded` carries which — because a record that is not on file is
 * not work anybody owes.
 *
 * ── The manifest-row truth rule ──────────────────────────────────────────────────────────────
 *
 * A manifest row carries the SERVER's truth; the canvas carries the ceremony's. The hire
 * purchase row is keyed to `contract_agreements.signed_at` + a non-empty `agreement_url` —
 * exactly what `sign_contract` reads — and annotates the ceremony beside it ("Executed 28 Jul ·
 * sealing · 12s"). `get_contract_agreements_status` goes green at countersign, before the seal
 * writes that URL; both facts are true and the row shows both rather than picking one.
 *
 * PURE, AND REACT-FREE. vitest runs node-env over `.ts` only, and every rule above is the kind
 * that must be pinned by a test rather than by a screenshot.
 */

import { differenceInCalendarDays, differenceInSeconds, format, isValid, parseISO } from 'date-fns';

import { driverProfileGapSentence } from '@/lib/driverProfile';
import { isThirdPartyVehicle } from '@/lib/agreements/constants';
import {
    INSTRUMENT_NOUNS as INSURANCE_INSTRUMENT_NOUNS,
    instrumentFor,
    type InsuranceInstrument,
} from '@/lib/agreements/instrument';
import { businessDay, formatCurrency } from '@/lib/format';

import { AREA_LABELS, SETUP_AREAS, type SetupArea } from './steps';
import {
    BLOCKER_ROWS,
    DEPOSIT_MISSING,
    DRIVER_PROFILE_INCOMPLETE,
    landRefusals,
    type LandedRefusal,
} from './signContract';
import type {
    ContractSetupStatus,
    SetupSectionKey,
    SetupStatusBlocker,
    SetupStatusSection,
} from './data/contract';

// ─── The row vocabulary ──────────────────────────────────────────────────────

export type ManifestRowId =
    | 'dep-payment'
    | 'dep-agreement'
    | 'veh-assign'
    | 'veh-service'
    | 'veh-registration'
    | 'veh-roadworthiness'
    | 'veh-tracker'
    | 'veh-odometer'
    | 'drv-name'
    | 'drv-contact'
    | 'drv-file'
    | 'ins-policy'
    | 'ins-premium'
    | 'ins-loan'
    | 'ins-loan-agreement'
    | 'fin-cash-price'
    | 'fin-price'
    | 'fin-instalment'
    | 'agr-parties'
    | 'agr-partner'
    | 'agr-hpa'
    | 'agr-witness';

export type RowClass = 'gate' | 'render' | 'record';

export type RowState = 'your-move' | 'waiting' | 'locked' | 'done';

/**
 * ── THE ROW TYPES CARRY THEIR VOCABULARY, AND THEY DEFAULT TO THIS ONE ───────────────────────
 *
 * `ManifestRow`, `AreaGroup`, `JumpTarget`, `Narrator` and `NarratorAction` are the SHELL's
 * vocabulary rather than the contract's: the same column, the same marks, the same narrator band
 * draw the advance dossier over a `loans` row, whose areas are `disbursement | repayment |
 * agreement` and whose row ids are its own. The two parameters say WHICH set of area keys and
 * WHICH set of row ids a given rail is made of.
 *
 * BOTH DEFAULT TO THE CONTRACT'S, so every existing call site — `ManifestRow`, `AreaGroup[]`,
 * `Record<ManifestRowId, …>` — means exactly what it meant before and compiles unchanged. Nothing
 * below is generic that has to KNOW the contract's areas: `SetupState`, `ActivationGate` and
 * `SetupProgress` stay concrete, because activation is the contract's own ceremony and an advance
 * has none.
 *
 * `extends string` IS LOAD-BEARING, not decoration: `manifestView`'s open/closed overrides are a
 * `Partial<Record<Area, boolean>>`, and a `Record` key parameter has to be assignable to
 * `string | number | symbol` before it can be written at all.
 */
export interface JumpTarget<Area extends string = SetupArea> {
    area: Area;
    /** The canvas block the row belongs to. */
    block: string;
    /** The field inside that block worth putting the caret in. */
    focus?: string;
}

export interface RowMeta {
    /** Who is being waited on, by name. */
    actor?: string;
    /** Whole days since the wait started. */
    elapsedDays?: number;
    /** When the link went out, ISO. */
    sentAt?: string;
    /** How many times the recipient opened it. */
    openCount?: number;
    /** What has to happen first, in words. Locked rows only. */
    unlockText?: string;
    /** The dated receipt on a settled row, already formatted. */
    doneDate?: string;
    /** An unsaved local edit. Never counts as progress. */
    draft?: string;
    /** Record rows: whether the thing is on file. */
    recorded?: boolean;
    /** Seconds the seal has been running, for the rows that show it. */
    sealElapsedSeconds?: number;
}

export interface ManifestRow<Area extends string = SetupArea, Id extends string = ManifestRowId> {
    id: Id;
    area: Area;
    label: string;
    cls: RowClass;
    /**
     * DORMANT: nothing is owed on this row and nothing the admin types can change that, so it is
     * out of both halves of its group's counter. NOTHING RENDERS FROM THIS FLAG — the manifest used
     * to draw "· optional" from it and does not any more, and may not: it survives as arithmetic
     * (the counters) and as the grey "nothing to do here yet" mark, and as those only.
     *
     * TWO CONTRACT SHAPES DECLARE IT. [D-B41]: the contract with no `contract_deposits` row, where
     * BOTH deposit rows are dormant — `sign_contract` no longer refuses over a missing deposit and
     * the Schedule credits GHS 0.00 without one, so there is no send to unblock and no paper to
     * chase. [D-B48]: the contract on one of Primelane's own vehicles, where the Vehicle Schedule
     * row is dormant — the partner side of that arrangement is Primelane itself, there is nobody to
     * countersign with, and `get_contract_setup_status` emits no arm over it. In both an admin
     * looking at the row has nothing in front of them, which is the one thing this flag has ever
     * meant. It is set by `rowOptionalOf`, not by `RowDef.optional`, because it is a property of the
     * contract rather than of the row.
     *
     * THE TWO CONDITIONAL AGREEMENTS ARE NOT IT, AND THE DISTINCTION IS THE WHOLE RULE. The
     * insurance-loan agreement stays counted and blue on a contract with no loan (owner's ruling,
     * 2026-08-04): a premium recorded on it may be financed, and a financed one is refused until
     * its agreement is sealed [D-B14] — work in front of the admin, not none. The deposit's paper
     * is dormant only because the deposit itself is.
     */
    optional: boolean;
    state: RowState;
    /** The right-hand annotation: a value, a receipt, a count. */
    detail: string;
    /** The sentence under the row, when there is one. */
    note: string | null;
    meta: RowMeta;
    jump: JumpTarget<Area>;
    /** The server's own refusal, verbatim, when the last activation attempt named this row. */
    refusal: string | null;
}

interface RowDef {
    id: ManifestRowId;
    area: SetupArea;
    label: string;
    cls: RowClass;
    /**
     * DECLARE A ROW DORMANT UNCONDITIONALLY — grey mark, out of both halves of its group's counter.
     *
     * NO ROW DECLARES IT, and the channel stays documented rather than deleted because dormancy
     * itself is live again: the two deposit rows wear it on a contract that took no deposit
     * [D-B41]. What they do NOT have is a static answer — the same two rows are counted and blue
     * the moment a payment is linked — so they are decided per contract in `rowOptionalOf` and this
     * field stays for a row that would be dormant on every contract there is.
     */
    optional?: boolean;
    block: string;
    focus?: string;
    /**
     * Days of SOMEBODY ELSE'S calendar this row costs once it is started.
     *
     * This is what "landing" is resolved on: an admin who opens the page cold should be put in
     * front of the thing with the longest external lead time, because that is the one whose
     * clock has not started yet. Zero means the row costs only typing.
     */
    leadDays: number;
}

/**
 * The 22 rows, in the owner's area order.
 *
 * IDs ARE STABLE AND PUBLIC. Refusals land on them, deep links focus them, and the prototype's
 * DOM ids are the same strings — renaming one silently unlinks a server sentence from the row
 * that can fix it.
 */
const ROW_DEFS: RowDef[] = [
    // 1 ── Driver
    { id: 'drv-name', area: 'driver', label: 'Legal name', cls: 'render', block: 'b-name', leadDays: 0 },
    { id: 'drv-contact', area: 'driver', label: 'Contact', cls: 'render', block: 'b-contact', focus: 'f-address', leadDays: 1 },
    { id: 'drv-file', area: 'driver', label: 'Driver file', cls: 'record', block: 'b-driverfile', leadDays: 1 },

    // 2 ── Deposit
    // BOTH CLASSES BELOW ARE THE UNCONDITIONAL HALF OF A CONDITIONAL ANSWER, and `rowClassOf` reads
    // the contract on top of them: the payment falls to `record` where no deposit is linked [D-B41]
    // and the paper rises to `gate` where one is [D-B15].
    { id: 'dep-payment', area: 'deposit', label: 'Deposit payment', cls: 'render', block: 'b-deposit-payment', leadDays: 3 },
    { id: 'dep-agreement', area: 'deposit', label: 'Deposit agreement', cls: 'record', block: 'b-deposit-agreement', leadDays: 2 },

    // 3 ── Vehicle
    { id: 'veh-assign', area: 'vehicle', label: 'Vehicle assigned', cls: 'render', block: 'b-assignment', leadDays: 0 },
    { id: 'veh-service', area: 'vehicle', label: 'Service and conversion', cls: 'gate', block: 'b-service', leadDays: 5 },
    { id: 'veh-registration', area: 'vehicle', label: 'Registration', cls: 'gate', block: 'b-registration', leadDays: 14 },
    { id: 'veh-roadworthiness', area: 'vehicle', label: 'Roadworthiness', cls: 'gate', block: 'b-roadworthiness', focus: 'f-rwDate', leadDays: 10 },
    { id: 'veh-tracker', area: 'vehicle', label: 'Primary tracker', cls: 'gate', block: 'b-trackers', leadDays: 2 },
    // THE ODOMETER IS A GATE THAT PRINTS ON NOTHING, AND IT STAYS A GATE. No instrument carries the
    // reading — the v1 Schedule's Vehicle particulars stop at the condition — but `sign_contract`
    // refuses on `VEHICLE_MILEAGE_MISSING`, and the reason it refuses is operational rather than
    // documentary: the handover reading is the baseline both sides argue wear, mileage and valuation
    // from once the car is out. `cls: 'gate'` is the server's fact, not this app's opinion.
    { id: 'veh-odometer', area: 'vehicle', label: 'Odometer at handover', cls: 'gate', block: 'b-odometer', focus: 'f-odometer', leadDays: 0 },

    // 4 ── Insurance
    { id: 'ins-policy', area: 'insurance', label: 'Policy on cover', cls: 'gate', block: 'b-policy', leadDays: 5 },
    { id: 'ins-premium', area: 'insurance', label: 'Premium payment', cls: 'record', block: 'b-premium', leadDays: 0 },
    // THESE TWO LABELS ARE THE ADVANCE'S, AND THEY ARE DEFAULTS RATHER THAN CONSTANTS [D-B45]:
    // `labelOf` renames both where the year's premium was paid in full by the driver. Every other
    // row's name is fixed.
    { id: 'ins-loan', area: 'insurance', label: 'Premium advance', cls: 'record', block: 'b-insloan', leadDays: 0 },
    { id: 'ins-loan-agreement', area: 'insurance', label: 'Insurance premium advance', cls: 'record', block: 'b-insloanagr', leadDays: 2 },

    // 5 ── Financials
    // THE CASH PRICE LEADS, BECAUSE THE CANVAS AND THE SCHEDULE BOTH LEAD WITH IT. `FinancialsArea`
    // draws the money clauses in the instrument's own order — cash price, Vehicle Purchase Price,
    // Deposit credited, Weekly Instalment — and the rail is read against that canvas, so a row order
    // of its own would have an admin walking the column in one order and the page in another.
    { id: 'fin-cash-price', area: 'financials', label: 'Cash price', cls: 'render', block: 'b-price', focus: 'f-cashPrice', leadDays: 0 },
    { id: 'fin-price', area: 'financials', label: 'Selling price', cls: 'render', block: 'b-price', focus: 'f-price', leadDays: 0 },
    { id: 'fin-instalment', area: 'financials', label: 'Weekly instalment and start date', cls: 'render', block: 'b-price', focus: 'f-instalmentAmount', leadDays: 0 },

    // 6 ── Agreements
    { id: 'agr-parties', area: 'agreements', label: 'Parties', cls: 'render', block: 'b-parties', focus: 'f-witnessAddress', leadDays: 1 },
    // THE SCHEDULE LEADS THE INSTRUMENTS BECAUSE IT IS SIGNED FIRST [D-B48]. No driver signs a hire
    // purchase agreement on a partner's vehicle before that partner has executed a Vehicle Schedule
    // about it: `agreement_sign` refuses the DRIVER seat with `AGREEMENT_PRIOR_SIGNATURE_REQUIRED`,
    // so the rail has to walk the admin down the column in the order the signatures are actually
    // taken. Its class is conditional (`rowClassOf`) — a gate on a third-party vehicle, a dormant
    // record on one of Primelane's own.
    { id: 'agr-partner', area: 'agreements', label: 'Vehicle schedule', cls: 'gate', block: 'b-partneragr', leadDays: 3 },
    { id: 'agr-hpa', area: 'agreements', label: 'Hire purchase agreement', cls: 'gate', block: 'b-hpa', leadDays: 3 },
    { id: 'agr-witness', area: 'agreements', label: 'Witness agreement', cls: 'gate', block: 'b-witnessagr', leadDays: 3 },
];

// ─── The server's blockers, and the rows that clear them ─────────────────────

/**
 * Manifest area → the payload section that counts the same work.
 *
 * FOUR OF THE SIX SHARE A NAME AND TWO DO NOT: the canvas calls the driver's area `driver` where
 * the payload calls it `parties` (it holds the witness too), and it calls the money area
 * `financials` where the payload calls it `terms`. Written out rather than inferred, because a
 * silent miss here would show one area's counts under another's heading.
 */
const AREA_SECTIONS: Record<SetupArea, SetupSectionKey> = {
    deposit: 'deposits',
    vehicle: 'vehicle',
    driver: 'parties',
    insurance: 'insurance',
    financials: 'terms',
    agreements: 'agreements',
};

/**
 * `_AWAITING_SEAL` code → the slot it is about, for reconciling the seal counter.
 *
 * ONE CODE PER DOC TYPE, WHICH IS WHY THIS IS A MAP AND NOT A COUNT. The server emits
 * `AGREEMENT_DEPOSIT_AWAITING_SEAL` once however many deposits are waiting on their file [D-B15],
 * so the deposit contributes one entry here exactly as the other three do — the panel says a seal
 * is running, not how many.
 */
const AWAITING_SEAL_DOC_TYPES: Record<string, AgreementDocTypeKey> = {
    AGREEMENT_DRIVER_AWAITING_SEAL: 'HIRE_PURCHASE',
    AGREEMENT_WITNESS_AWAITING_SEAL: 'WITNESS',
    AGREEMENT_INSURANCE_LOAN_AWAITING_SEAL: 'INSURANCE_LOAN',
    AGREEMENT_DEPOSIT_AWAITING_SEAL: 'DEPOSIT',
    AGREEMENT_PARTNER_AWAITING_SEAL: 'PARTNER_MANAGEMENT',
};

/**
 * PRIMELANE'S OWN PARTNER ROW — the house, and the one vehicle owner that signs nothing.
 *
 * `20260908010000` marks it with `partners.is_house` and guards the column three ways: a partial
 * unique index admitting at most one house row, a BEFORE UPDATE trigger refusing a true→false
 * demotion, and a post-check that the marked row exists. THE SERVER IS THE TRUTH —
 * `agreement_create` refuses a house vehicle with `AGREEMENT_PARTNER_IS_HOUSE` and
 * `get_contract_setup_status` tests `is_house` rather than an id — and `isThirdPartyVehicle` is
 * the board's mirror of it, so a row is never OFFERED into a refusal. The two can only disagree if
 * somebody promotes a second house partner, which the index forbids.
 *
 * THE ID IS THE MIRROR'S ONLY AVAILABLE SPELLING. `partners.is_house` is not on the contract read
 * this page makes — the snapshot carries the vehicle's `partner_id` and no partner row at all —
 * and widening that select to reach `partners` would fail with 42703 on every deployment where the
 * migration has not landed, taking the whole Dossier down for a grey mark.
 *
 * ONE SPELLING, IMPORTED, AND NOT A LOCAL COPY. `@/lib/agreements/constants` is where the board
 * declares this predicate, and the Dossier reads the same function rather than restating it: two
 * spellings of one rule is how the manifest and the canvas come to disagree about whether a
 * contract owes a schedule at all. The icon imports that module carries are no obstacle to a
 * node-env vitest run — `boardSlots.ts` imports it and `boardSlots.test.ts` passes on that graph.
 *
 * ITS NULL ARM IS THE CONSERVATIVE ONE, WHICH MATTERS HERE MORE THAN ON THE BOARD. `undefined`
 * answers FALSE, so a hand-built snapshot that forgot `partnerId` is graded one of Primelane's own
 * rather than a third party — a grey row instead of a gate invented out of a missing field.
 * `VehicleFacts.partnerId` is required so that tsc catches the omission first.
 */

/** The receipt a satisfied group collapses to. */
const AREA_RECEIPTS: Record<SetupArea, string> = {
    deposit: 'Deposit received',
    vehicle: 'Ready for handover',
    driver: 'Complete',
    insurance: 'Cover bound',
    financials: 'Money clauses set',
    agreements: 'Executed',
};

// ─── The snapshot every row is derived from ──────────────────────────────────

export interface PrepFlag {
    item: string;
    is_complete: boolean;
    /** When the item was recorded, if the caller could find it. Shown as the row's receipt. */
    completedAt?: string | null;
}

export interface DepositFact {
    id: number;
    amount: number | null;
    paidOn: string | null;
}

export interface VehicleFacts {
    id: number | null;
    make: string | null;
    model: string | null;
    /** The plate, from `vehicles.registration_id → vehicle_registrations.number`. */
    registration: string | null;
    /** `contracts.vehicle_mileage` — what `{{vehicle.mileage}}` actually reads. */
    mileage: number | null;
    /**
     * `vehicles.partner_id` — WHO OWNS THE CAR, and therefore whether this contract owes a Vehicle
     * Schedule at all [D-B48].
     *
     * REQUIRED, so a hand-built snapshot that forgets it fails the typecheck instead of quietly
     * reporting every vehicle as one of Primelane's own — which is the direction that silently
     * drops a gate the server is holding the activation on. NULL means no vehicle is assigned yet
     * (or a read that did not ask), and `isThirdPartyVehicle` answers false for it: a contract with
     * no car owes no schedule about one.
     */
    partnerId: number | null;
}

export interface DriverFacts {
    id: number | null;
    /**
     * THE TWO HALVES OF THE NAME, KEPT APART — `fullName` is the join, and a join cannot be undone.
     *
     * The server grades `first_name` and `last_name` as two separate `IS NOT NULL`s, so a driver
     * with a first name and no surname reads as NAMED through `fullName` while the activation is
     * refused over the half that is missing. `driverProfileGapSentence` needs the halves to say
     * WHICH one, and this is the only place on the page they still exist.
     *
     * REQUIRED, so that a hand-built snapshot which forgets them fails the typecheck instead of
     * quietly reporting a named driver's name as absent.
     */
    firstName: string | null;
    lastName: string | null;
    fullName: string | null;
    phone: string | null;
    address: string | null;
    email: string | null;
    birthDate: string | null;
    photo: string | null;
    repaymentTag: string | null;
    licenceOnFile: boolean;
    nationalIdOnFile: boolean;
}

export interface WitnessFacts {
    id: number | null;
    name: string | null;
    phone: string | null;
    address: string | null;
}

export interface InsuranceFacts {
    junctionId: number | null;
    policyStartsAt: string | null;
    policyValuedAt: number | null;
    premium: { amount: number | null; paidOn: string | null } | null;
    discount: { amount: number | null } | null;
    loan: {
        amount: number | null;
        rate: number | null;
        /**
         * `insurance_loan_instalments`, THE ADVANCE'S RATE HISTORY — never an amortisation table.
         *
         * REQUIRED, AND EMPTY WHERE THERE IS NO SCHEDULE. `advanceRepayments` filters this array
         * unguarded, so an absent one is a TypeError rather than a missing figure; the adapter
         * therefore always supplies `[]` and the type says so, which is what makes a hand-built
         * snapshot that forgets it fail the typecheck instead of the dialog.
         *
         * `startsAt` is a DATE column, so it arrives as `'YYYY-MM-DD'` — never an instant, and a
         * string comparison over it is the chronological one.
         *
         * IT IS ALSO THE INSTRUMENT'S SECOND LIMB [D-B45]: `instrumentFor` asks how many
         * `insurance_loan_instalments` rows the loan carries, and that count is this array's
         * length. No separate field — two spellings of one number is how they come to disagree.
         */
        instalments: { amount: number | null; startsAt: string | null }[];
        /**
         * Σ of the TRANSACTIONS behind this loan's `insurance_loan_deposits` — the first limb of
         * the instrument [D-B45], and 0 where the driver has paid nothing.
         *
         * REQUIRED AND NEVER OPTIONAL, so a hand-built snapshot that omits it fails the typecheck
         * rather than quietly naming every premium an advance. The adapter always supplies a
         * number: an embed the select did not ask for resolves to `[]`, which is 0, which is the
         * advance — the conservative answer rather than a silent one.
         */
        depositTotal: number;
        /**
         * WHEN THE PREMIUM WAS PAID — the LATEST deposit transaction's `transaction_at`, still an
         * INSTANT, and null where there is no deposit.
         *
         * The latest and not the first, because a premium can be paid in two goes and the day it
         * was COMPLETED is the day the Record states it was paid. Printed through `dateText`, which
         * is what puts it on the Ghana business day.
         */
        depositPaidOn: string | null;
    } | null;
}

export interface FinancialFacts {
    /**
     * `contracts.cash_price` — THE STATUTORY PRICE, STORED AND NEVER DERIVED.
     *
     * NRCD 292 s.3 wants it stated beside the hire-purchase price, and the difference between the
     * two IS the charge for credit — so `agreement_merge_data` reads this column straight and
     * resolves NULL where it is unset rather than falling back to `sold_at`. Two equal prices on a
     * signed page state that Primelane charged nothing for the credit.
     *
     * REQUIRED AND NULLABLE, exactly like `sellingPrice` beside it: both are nullable columns of
     * the same row that the adapter always maps, so an omission is a fixture that forgot rather
     * than a contract without a price.
     */
    cashPrice: number | null;
    /** `contracts.sold_at` — the hire-purchase price. */
    sellingPrice: number | null;
    instalment: { amount: number | null; startsAt: string | null } | null;
    /** NOT NULL DEFAULT 365 in SQL, so it can never be missing — a check, never a fill. */
    overdueRate: number | null;
}

export interface SignatureFact {
    party: string;
    status: string;
    signedAt: string | null;
}

export interface DocumentFact {
    id: number;
    status: string;
    finalizedAt: string | null;
    /** What `agreement_render` says the wording is still missing. */
    missing: string[] | null;
    signatures: SignatureFact[];
}

export interface TokenFact {
    party: string;
    sentAt: string | null;
    lastOpenedAt: string | null;
    openCount: number;
}

export type AgreementDocTypeKey =
    | 'HIRE_PURCHASE'
    | 'WITNESS'
    | 'DEPOSIT'
    | 'INSURANCE_LOAN'
    | 'LOAN'
    | 'PARTNER_MANAGEMENT';

export interface AgreementFact {
    docType: AgreementDocTypeKey;
    subjectId: number;
    /**
     * WHICH INSURANCE INSTRUMENT THIS SLOT IS [D-B45] — set on `INSURANCE_LOAN` and null on every
     * other type, carried off the board rather than re-derived.
     *
     * OPTIONAL, AND ABSENT FALLS TO THE ADVANCE, exactly as the two cross-document facts below fall
     * to "nobody has signed": a snapshot assembled by hand gets the shape this app has always
     * drawn, which withholds nothing and claims nothing new about the driver's money.
     */
    instrument?: InsuranceInstrument | null;
    /** The legacy row `sign_contract` reads. Both halves are the gate. */
    legacySignedAt: string | null;
    legacyUrl: string | null;
    document: DocumentFact | null;
    tokens?: TokenFact[];
    /**
     * THE TWO CROSS-DOCUMENT FACTS [D-B18], CARRIED RATHER THAN RE-DERIVED.
     *
     * Facts about the CONTRACT, hung on every slot by `buildBoardSlots` from the same rows
     * `agreement_sign` reads — a SIGNED seat on the live document of that type, or a `signed_at` on
     * the legacy `contract_agreements` row of that type. `snapshotAdapter.agreementFacts` copies
     * them straight across, so this layer READS the board's derivation instead of running a second
     * one over the same rows: two readings that could disagree the moment either moved.
     *
     * OPTIONAL, AND ABSENT MEANS FALSE. A snapshot assembled by hand (a test fixture, a caller that
     * predates this field) is treated as "nobody has signed", which locks the witness agreement and
     * withholds a countersignature rather than offering either into a server refusal.
     */
    driverSigned?: boolean;
    witnessSigned?: boolean;
    /**
     * THE FOURTH CONTRACT FACT, CARRIED FOR THE REASON THE TWO ABOVE ARE [D-B53] — this contract's
     * hire purchase agreement came back on paper: a paper-origin signed `contract_agreements`
     * DRIVER row AND no live hire purchase document. `buildBoardSlots` derives the conjunction off
     * the same two sources `agreement_sign` reads and hangs it on every slot; re-deriving it here
     * would be a second answer over the same rows.
     *
     * IT IS READ OFF THE SLOT IN HAND, NOT `some`-ed OVER THE BOARD like the two signature facts,
     * because the only rule that asks it — `countersignBlockedNote`'s witness arm — already holds
     * the row it is about. The board's own reader does the same (`siblingSignatureOutstanding`).
     *
     * OPTIONAL, AND ABSENT MEANS FALSE: a snapshot assembled by hand keeps the witness arm and goes
     * on withholding the countersignature, which is the direction that never offers a button into a
     * server refusal.
     */
    masterOnPaper?: boolean;
}

export interface SetupSnapshot {
    contractId: number;
    /** The latest `contract_updates` status. 'DRAFT' when the contract has never been updated. */
    status: string;
    /** The clock. Every elapsed figure is measured from here, which is what makes this pure. */
    now?: Date | string;
    prep: PrepFlag[];
    vehicle: VehicleFacts;
    driver: DriverFacts;
    witness: WitnessFacts | null;
    deposits: DepositFact[];
    insurance: InsuranceFacts | null;
    financials: FinancialFacts;
    agreements: AgreementFact[];
    /**
     * THE BOARD'S READ FAILED — SLOTS EXIST, BUT NOTHING THEY SAY IS ESTABLISHED [D-B57].
     *
     * `useContractAgreements` mints slots from whichever of its reads came back, so a refused
     * documents read still yields a full board whose document-shaped facts were computed over an
     * empty list. `masterOnPaper` is the one that turns on such an absence — its second limb is
     * "no live hire purchase document", which is vacuously true over nothing — so a caller that
     * declares an arrangement off it has to ask this first. The board's own readers already do
     * (`useInsuranceState`'s `hpClass`, the insurance area's own `hpOnPaper`).
     *
     * OPTIONAL, AND ABSENT MEANS THE READ SUCCEEDED, which is the shape of every hand-assembled
     * fixture and of every caller that predates this field.
     */
    agreementsUnread?: boolean;
    /** Unsaved local edits, by row. Shown as a modifier; never counted as progress. */
    drafts?: Partial<Record<ManifestRowId, string>>;
    /** The last `sign_contract` refusal, verbatim, in the server's evaluation order. */
    refusals?: string[];
    /**
     * `get_contract_setup_status` — THE AUTHORITY ON WHETHER THIS CONTRACT MAY BE ACTIVATED.
     *
     * Null while the read is in flight, and null against a server that predates `20260802020000`.
     * Everything below degrades to the client's own derivation in that case, and the page is never
     * blank.
     *
     * THE FALLBACK IS THE OLD BEHAVIOUR PLUS ONE GATE, NOT THE OLD BEHAVIOUR EXACTLY. The
     * insurance-loan agreement row is the exception, and a deliberate one — see "THE ONE GATE THE
     * CLIENT NEVER HAD" in `buildSetupState`, where a null payload falls to the same seal test
     * `sign_contract` applies rather than to the record derivation that read `done` on an unsigned
     * agreement. The difference only ever runs one way: a page that has not heard from the server
     * is STRICTER than one that has, never more permissive. It cannot dead-end an admin either,
     * because `canActivate` stays null without a payload and the activation control only pre-refuses
     * on an explicit `false`.
     */
    serverStatus?: ContractSetupStatus | null;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface AreaGroup<Area extends string = SetupArea, Id extends string = ManifestRowId> {
    area: Area;
    label: string;
    /** 1-based, for the numeral the manifest prints. */
    index: number;
    rows: ManifestRow<Area, Id>[];
    /**
     * The counter, and it counts THE ROWS THE GROUP DRAWS: `isCountedRow` puts every non-optional
     * row in the denominator and `isRowSettled` — the predicate the row's own mark is drawn from —
     * decides which of them fill the numerator. So the second number is the list underneath it and
     * the first can never claim a row the admin can see is empty.
     */
    doneCount: number;
    requiredCount: number;
    /** Every row settled — the group collapses to its receipt. */
    satisfied: boolean;
    /** The one-line dated receipt a satisfied group collapses to. */
    receipt: string | null;
    /**
     * The server's own n-of-m for this area, where it is richer than the row count beside it.
     *
     * IT COUNTS ARMS, NOT ROWS, and the two are not the same picture. The insurance area draws five
     * rows of which four are records, so the counter beside it reads one of four on a contract with
     * a policy and no premium, no discount and no loan, while the server reads two of six. That is
     * also why the payload cannot BE the counter: the arms do not correspond to rows, and this is
     * null until the server has answered. Null too for an area with no section of its own.
     */
    serverCounts: { done: number; total: number } | null;
}

// ─── The two predicates the counter and the mark BOTH read ───────────────────

/**
 * IS THIS ROW DRAWN AS DONE — the single predicate behind the mark and the group counter.
 *
 * IT EXISTS BECAUSE THE TWO ONCE ANSWERED SEPARATELY AND DISAGREED IN PUBLIC. The counter read
 * `state === 'done'` while the mark read this ladder, and a record row is `done` by construction
 * whether or not the thing is on file — so a fresh contract drew `Not on cover · Not recorded ·
 * Not financed · Not on file` under the heading `Insurance 3/4`. Three rows the admin could see
 * were empty were being counted as finished work.
 *
 * `markFor` is written on top of this, so the number and the dot cannot come apart again: a row
 * counts as done exactly when it is DRAWN done, refusals and unsaved drafts included — a draft is
 * a value that is not saved yet, and the page has always said it is never progress.
 *
 * IT READS ANY RAIL'S ROW, which is what the widened parameter says. The predicate asks about the
 * row's STATE, its class and its meta and never about which area or which id it carries, so the
 * advance dossier's rows go through this one rather than through a second ladder that could grade
 * the same shape differently.
 */
export function isRowSettled(row: ManifestRow<string, string>): boolean {
    if (row.refusal) return false;
    if (row.meta.draft) return false;
    if (row.state !== 'done') return false;
    // A record that is not on file is settled — nobody owes it — but it is not DONE WORK, and
    // `isCountedRow` keeps it out of the counter rather than this line calling it finished.
    return !(row.cls === 'record' && row.meta.recorded === false);
}

/**
 * DOES THIS ROW BELONG IN THE GROUP COUNTER — is it a row the group DRAWS as required?
 *
 * THE DENOMINATOR IS WHAT THE ADMIN CAN SEE AND COUNT. `Insurance 0/1` over five visible lines is
 * as confusing as `3/4` over four empty ones — the second number has to be the rows underneath it,
 * or the counter is describing a list nobody is looking at. So every non-dormant row counts,
 * RECORDS INCLUDED, and only DORMANCY takes a row out of both halves — a document whose subject
 * does not exist AND WHICH NOTHING IN THIS SETUP OWES cannot look overdue, which is the one
 * exclusion this counter has always had.
 *
 * ONE EXCLUSION IS LIVE, AND IT IS A PROPERTY OF THE CONTRACT [D-B41]: a contract that took no
 * deposit drops BOTH deposit rows out of the counter, so the group reads its receipt instead of
 * `0/2` over two lines nobody owes. Everything else is in. The insurance-loan agreement in
 * particular is counted and blue on a contract with no loan (owner's ruling, 2026-08-04) — a
 * premium recorded here may be financed, and a financed one is refused until its agreement is
 * sealed [D-B14] — so insurance reads 0/4 on a fresh contract while the deposit reads nothing at
 * all. Dormancy is arithmetic and a grey dot: no surface prints the word "optional", ever.
 *
 * A RECORD FILLS THE NUMERATOR WHEN IT IS ACTUALLY ON FILE, through `isRowSettled`, so the counter
 * moves exactly when a line the admin is reading changes from `Not recorded` to a value.
 *
 * `n/m` WITH m > n ON A SATISFIED GROUP IS NOT A CONTRADICTION, and the column is what makes it
 * coherent: a satisfied group is COLLAPSED to its dated receipt (`isGroupOpen`, and `Manifest.tsx`
 * prints the receipt INSTEAD of the counter there), so `Cover bound Jul 26, 2026` is what the rail
 * shows. The counter only reappears where somebody deliberately opened the group — and then the
 * rows it counts are on screen directly beneath it, `Not recorded` and all.
 *
 * The server's own n-of-m is not the source: it counts ARMS, not rows (six against five in
 * insurance), and it is null until the payload lands, so the counter would flicker and then
 * disagree with the list beside it.
 *
 * IT READS ANY RAIL'S ROW, for the reason `isRowSettled` does: the question is the dormancy flag
 * and nothing else about which rail the row came off.
 */
export function isCountedRow(row: ManifestRow<string, string>): boolean {
    return !row.optional;
}

export interface SetupProgress {
    areasComplete: number;
    areaCount: number;
    needYou: number;
    waitingOnOthers: number;
    /** Rows that are not settled, in any of the three unsettled states. */
    outstanding: number;
}

export type NarratorIntent = 'jump' | 'activate' | 'open-receipt' | 'resend-link' | 'copy-link';

export interface NarratorAction<
    Area extends string = SetupArea,
    Id extends string = ManifestRowId,
> {
    id: string;
    label: string;
    kind: 'primary' | 'ghost';
    intent: NarratorIntent;
    rowId?: Id;
    area?: Area;
}

export interface Narrator<Area extends string = SetupArea, Id extends string = ManifestRowId> {
    /** One sentence of ground truth. Plain text — the band decides what to embolden. */
    sentence: string;
    /** Never more than two. */
    actions: NarratorAction<Area, Id>[];
}

export interface SealPending {
    /** How many documents are countersigned but not yet sealed. */
    count: number;
    /** When the last signature landed, ISO — what the elapsed counter runs from. */
    startedAt: string | null;
    elapsedSeconds: number;
}

export interface ActivationGate {
    /** Every gate met, nothing sealing, and the contract still in DRAFT. */
    ready: boolean;
    /**
     * `get_contract_setup_status.can_activate`, verbatim — null until the server has answered.
     *
     * SEPARATE FROM `ready` BECAUSE THE TWO ANSWER DIFFERENT QUESTIONS. `ready` is "should the
     * button be offered", which is false on an already-activated contract; this is "would the
     * server take the call", which the activation control refuses on.
     */
    canActivate: boolean | null;
    /**
     * The server's blocker list, verbatim, and empty until it has answered — with ONE documented
     * exception: `DRIVER_PROFILE_INCOMPLETE` carries the fields that are actually absent rather
     * than the server's fixed four-field sentence. See `spokenBlockers`, which is the only place
     * that substitution is made, and which falls back to the server's own wording the moment the
     * client cannot name a gap.
     */
    blockers: SetupStatusBlocker[];
    unmetCount: number;
    unmet: ManifestRow[];
    /** The contract has already left DRAFT. */
    activated: boolean;
    sealPending: SealPending | null;
    refusals: LandedRefusal[];
}

export interface SetupState {
    rows: ManifestRow[];
    areaGroups: AreaGroup[];
    progress: SetupProgress;
    narrator: Narrator;
    gate: ActivationGate;
}

// ─── The one activation verdict ──────────────────────────────────────────────

/**
 * A UNION AND NOT A PAIR OF FIELDS: a blocked verdict ALWAYS carries the sentence that says why,
 * so neither surface has to invent a fallback and neither can print a blank refusal.
 */
export type ActivationVerdict =
    | { blocked: false; reason: null }
    | { blocked: true; reason: string };

/**
 * WHETHER ACTIVATION MAY BE OFFERED — ASKED ONCE, ANSWERED ONCE.
 *
 * THIS EXISTS BECAUSE THE PAGE SHIPPED TWO ACTIVATE BUTTONS THAT DISAGREED. The pinned footer
 * (`gateFooter`) read `gate.canActivate` and greyed itself out with the server's sentence
 * underneath; the header band (`narratorFor`) reached its "Every gate is met. This contract is
 * ready to activate." branch off the CLIENT's gate rows alone and offered a live Activate beside
 * it. On contract 309 both were on screen at once — "Every gate is met" above an enabled button,
 * "Complete the driver's profile…" below a dead one. Both call this now, so a disagreement is not
 * a bug that can recur: it is a state this function cannot return.
 *
 * THE STRICTER READING WINS, AND ONLY ONE OF THE TWO IS ALLOWED TO BE STRICT. `can_activate` is
 * the server's own answer to the exact question the button asks, computed from live rows — so a
 * `false` from it silences a clean rail (the rail counts GATE rows, and most of the payload's arms
 * — the driver's profile among them — land on rows that are not gates, which is how it reads zero
 * unmet under a refusal). The reverse is deliberately NOT true: unmet gate rows do not block, and
 * must not start. The control has been clickable with gates outstanding since day one, on the
 * standing rule that the client does not own the gate list and pressing it is a legitimate way to
 * ask the server — a button greying itself out on a client-side count is the failure this page was
 * rebuilt to end.
 *
 * `=== false`, NEVER `!canActivate`: null is "the server has not answered", and a page that has not
 * heard back never pre-refuses.
 */
export function activationVerdict(gate: ActivationGate): ActivationVerdict {
    if (gate.canActivate !== false) return { blocked: false, reason: null };
    const first = gate.blockers[0];
    if (!first) return { blocked: true, reason: 'The server will not activate this contract yet.' };
    // The message as `spokenBlockers` settled it: the server's sentence verbatim, or — for the
    // driver-profile arm alone — the same refusal narrowed to the fields that are actually absent.
    // Nothing is worded here, so this surface and the footer cannot come apart.
    return {
        blocked: true,
        reason:
            gate.blockers.length === 1
                ? first.message
                : `${first.message} · ${gate.blockers.length - 1} more`,
    };
}

// ─── Small helpers ───────────────────────────────────────────────────────────

function asDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : parseISO(value);
    return isValid(date) ? date : null;
}

/**
 * The app's one date shape, read in the BUSINESS zone. An unparseable value prints nothing rather
 * than "Invalid Date".
 *
 * `businessDay`, NOT `asDate`, AND THE DIFFERENCE IS A DAY. The values that reach here are a mix of
 * TIMESTAMPTZ instants — `insurances.starts_at`, which the policy form fills from a date input, so
 * the app reads back Accra midnight — and DATE strings (`contract_instalments.starts_at`). Rendering
 * the instants through the HOST zone printed `Cover bound Jul 25, 2026` for cover bound on the 26th
 * anywhere west of Greenwich. `businessDay` lands both shapes on the Ghana calendar day the record
 * carries, as a Date at local midnight, so the date-fns pattern below is unchanged.
 */
function dateText(value: string | Date | null | undefined): string {
    const date = businessDay(value);
    return date ? format(date, 'MMM d, yyyy') : '';
}

/**
 * "today" / "yesterday" / "Tuesday" / "Jul 12, 2026" — how a wait is spoken about.
 *
 * ON THE BUSINESS CALENDAR, BOTH THE COUNT AND THE NAME. `asDate` handed date-fns a host-zone
 * Date, so `Sent Friday` for a link sent on Friday in Accra read `Sent Saturday` anywhere east of
 * Greenwich — and the same day count decides `today`/`yesterday` and the row's `elapsedDays`
 * beside it. `businessDay` lands both ends on the Ghana day as Dates at local midnight, so the
 * date-fns patterns below are unchanged, and the count still comes off the injected `now`.
 */
function whenText(value: string | Date | null | undefined, now: Date): string {
    const date = businessDay(value);
    const today = businessDay(now);
    if (!date || !today) return '';
    const days = differenceInCalendarDays(today, date);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return format(date, 'EEEE');
    return format(date, 'MMM d, yyyy');
}

function money(amount: number | null | undefined): string {
    return amount === null || amount === undefined ? '' : formatCurrency(amount);
}

function plural(count: number, one: string, many: string): string {
    return count === 1 ? one : many;
}

function isFilled(value: string | null | undefined): boolean {
    return !!value && value.trim().length > 0;
}

/** The server gate on an agreement: a signature date AND a stored PDF. Both halves, always. */
function isSealedForActivation(slot: AgreementFact | null): boolean {
    return !!slot && !!slot.legacySignedAt && isFilled(slot.legacyUrl);
}

const PARTY_FALLBACKS: Record<string, string> = {
    DRIVER: 'the driver',
    WITNESS: 'the witness',
    COMPANY_REP: 'Primelane',
    BORROWER: 'the borrower',
    PARTNER: 'the partner',
};

function partyName(party: string, snapshot: SetupSnapshot): string {
    if (party === 'DRIVER' || party === 'BORROWER') return snapshot.driver.fullName || PARTY_FALLBACKS[party];
    if (party === 'WITNESS') return snapshot.witness?.name || PARTY_FALLBACKS.WITNESS;
    return PARTY_FALLBACKS[party] ?? party;
}

const SIGNED_STATUSES = new Set(['SIGNED']);

/**
 * THE TWO CROSS-DOCUMENT FACTS [D-B18], READ OFF THE SLOTS RATHER THAN DERIVED AGAIN.
 *
 * `buildBoardSlots` derives both from the rows `agreement_sign` itself reads — a SIGNED seat on the
 * live document of that type, or a `signed_at` on the legacy `contract_agreements` row of that type,
 * which for the 174 paper-era production contracts is the only source that will ever exist — and
 * hangs them on every slot the contract has. `snapshotAdapter.agreementFacts` carries them here, so
 * there is ONE derivation on this layer and not two readings that could drift apart the moment
 * either moved.
 *
 * THEY ARE CONTRACT FACTS, so any slot answers for the whole board and `some` is the right reader:
 * a contract whose witness agreement has not been minted still knows whether the driver signed.
 * An empty board, or a snapshot assembled without the fields, answers false — which locks rather
 * than offers.
 */
function partySignedTheirInstrument(
    snapshot: SetupSnapshot,
    fact: 'driverSigned' | 'witnessSigned',
): boolean {
    return snapshot.agreements.some((slot) => slot[fact] === true);
}

/** The driver has signed the hire purchase agreement — the witness agreement's whole dependency. */
function driverSignedHirePurchase(snapshot: SetupSnapshot): boolean {
    return partySignedTheirInstrument(snapshot, 'driverSigned');
}

/** The witness has signed the witness agreement — the other half of the countersign rule. */
function witnessSignedWitnessAgreement(snapshot: SetupSnapshot): boolean {
    return partySignedTheirInstrument(snapshot, 'witnessSigned');
}

const WITNESS_BEFORE_COUNTERSIGN =
    'The witness has to sign the witness agreement before Primelane can countersign this.';
const DRIVER_BEFORE_COUNTERSIGN =
    'The driver has to sign the hire purchase agreement before Primelane can countersign this.';

/**
 * Why Primelane cannot countersign this instrument yet, or null.
 *
 * THE MANIFEST HAS THE SAME DEAD-BUTTON PROBLEM THE BOARD HAS. `agreement_sign` refuses a
 * countersignature on the hire purchase agreement, the witness agreement and the insurance premium
 * advance [D-B27] until BOTH contract instruments have been signed by their own party, so a row
 * that read "every other party has signed — countersign as Primelane" off its own seats would be
 * pointing the admin at a refusal, and the narrator would then lead with it instead of with the
 * signature actually outstanding.
 *
 * ON THE TWO CONTRACT INSTRUMENTS ONLY THE SIBLING'S ARM IS ASKED, because each one's own
 * counterparties are its own arm — see `countersignReadyFor`. ON THE ADVANCE BOTH ARE SIBLINGS:
 * its seats are the borrower and Primelane, and a signed borrower is evidence about neither
 * contract instrument. The driver's arm is named FIRST when both are outstanding, which is the
 * dependency's own order — the witness cannot sign until the driver has, so naming the witness
 * would send an admin to a door that is also shut.
 *
 * THE VEHICLE SCHEDULE IS NOT ONE OF THEM, AND ITS ABSENCE IS A DECISION [D-B48]. [D-B27]'s
 * conjunction names three documents — the hire purchase agreement, the witness agreement and the
 * insurance premium advance — and `agreement_sign` was not widened to name a fourth. It could not
 * usefully be: the schedule is executed BEFORE any of those three exists, because the driver's own
 * signature is refused until the partner's is taken, so a rule making Primelane's countersignature
 * on the schedule wait for the driver would be a deadlock rather than an ordering. This function
 * therefore falls through to `null` for `PARTNER_MANAGEMENT`, and the countersign button on that
 * row is offered the moment the partner has signed.
 */
function countersignBlockedNote(slot: AgreementFact | null, snapshot: SetupSnapshot): string | null {
    if (slot?.docType === 'HIRE_PURCHASE' && !witnessSignedWitnessAgreement(snapshot)) {
        return WITNESS_BEFORE_COUNTERSIGN;
    }
    if (slot?.docType === 'WITNESS' && !driverSignedHirePurchase(snapshot)) {
        return DRIVER_BEFORE_COUNTERSIGN;
    }
    if (slot?.docType === 'INSURANCE_LOAN') {
        if (!driverSignedHirePurchase(snapshot)) return DRIVER_BEFORE_COUNTERSIGN;
        // THE PAYMENT RECORD ASKS THE DRIVER'S ARM ALONE [D-B45] — the third mirror of D-B27 in
        // this app, and the one that speaks. `agreement_sign` skips the witness limb on a document
        // whose `template_key` is `insurance_payment` and `siblingSignatureOutstanding` skips it on
        // the board, so a manifest that still printed "the witness has to sign" would be narrating
        // a refusal over a row whose countersign button is correctly offered. D-B27's rationale is
        // the credit exposure a third bargain creates; nothing is advanced under the Record.
        if (slot.instrument === 'PAYMENT_RECORD') return null;
        // AND THE PAPER MASTER DROPS THE WITNESS ARM TOO [D-B53], the server's second carve-out and
        // the same mirror problem. `agreement_sign` skips the witness limb on an advance whose
        // contract carries a paper-origin signed DRIVER row and no live hire purchase document, and
        // `siblingSignatureOutstanding` skips it on the board off this very field — so without the
        // waiver here the one page that mounts both surfaces says "the witness has to sign" in the
        // manifest over a row whose countersign button the board is correctly offering, on 174 of
        // the 232 paper-era contracts. A witness agreement attests to an IN-APP driver signature and
        // will never be generated for one executed offline; the driver's arm above is untouched, and
        // on these contracts it is satisfied by the very row this waiver reads.
        if (!witnessSignedWitnessAgreement(snapshot) && !slot.masterOnPaper) {
            return WITNESS_BEFORE_COUNTERSIGN;
        }
    }
    return null;
}

function signedCount(document: DocumentFact | null): number {
    return (document?.signatures ?? []).filter((signature) => SIGNED_STATUSES.has(signature.status)).length;
}

/** The next party in signing order who has not signed. Signature order is the template's. */
function pendingSignature(document: DocumentFact | null): SignatureFact | null {
    return (
        (document?.signatures ?? []).find(
            (signature) => !SIGNED_STATUSES.has(signature.status) && signature.status !== 'VOIDED',
        ) ?? null
    );
}

function newestSignedAt(document: DocumentFact | null): string | null {
    const dates = (document?.signatures ?? [])
        .map((signature) => signature.signedAt)
        .filter((value): value is string => !!value)
        .sort();
    return dates.length ? dates[dates.length - 1] : null;
}

function tokenFor(slot: AgreementFact | null, party: string | null): TokenFact | null {
    if (!slot || !party) return null;
    return (slot.tokens ?? []).find((token) => token.party === party) ?? null;
}

function slotsOf(snapshot: SetupSnapshot, docType: AgreementDocTypeKey): AgreementFact[] {
    return snapshot.agreements.filter((slot) => slot.docType === docType);
}

function slotOf(snapshot: SetupSnapshot, docType: AgreementDocTypeKey): AgreementFact | null {
    return slotsOf(snapshot, docType)[0] ?? null;
}

/**
 * WHICH INSURANCE INSTRUMENT THIS CONTRACT'S PREMIUM YEAR IS RECORDED ON [D-B45] — one answer, for
 * every row and label on this rail that has to name it.
 *
 * THE BOARD'S ANSWER FIRST, BECAUSE IT IS THE ONE THE SEND CONFIRMATION AND THE SERVER WILL USE.
 * `buildBoardSlots` derived it from the loan's own deposits and instalments at the same moment it
 * minted the slot; reading it back is what keeps the manifest and the board naming one document.
 *
 * THE SNAPSHOT'S LOAN IS THE FALLBACK, and it is the same loan by construction [D-B17]: both come
 * off the junction `useContractInsurance` resolved, so this only decides the answer where no slot
 * exists yet — a hand-built snapshot, or a subject read still in flight. With no loan at all the
 * limbs are unknown and `instrumentFor` answers `ADVANCE`, which is what this rail has always said.
 */
function insuranceInstrumentOf(snapshot: SetupSnapshot): InsuranceInstrument {
    const slot = slotsOf(snapshot, 'INSURANCE_LOAN').find((row) => !!row.instrument);
    if (slot?.instrument) return slot.instrument;

    const loan = snapshot.insurance?.loan ?? null;
    return instrumentFor({
        amount: loan?.amount ?? null,
        depositTotal: loan?.depositTotal ?? null,
        // The count IS the array's length — `insurance_loan_instalments` is selected unfiltered, so
        // an empty list is a counted zero rather than an unread one.
        instalmentCount: loan ? loan.instalments.length : null,
    });
}

function prepComplete(snapshot: SetupSnapshot, item: string): boolean {
    return !!snapshot.prep.find((flag) => flag.item === item)?.is_complete;
}

function prepDate(snapshot: SetupSnapshot, item: string): string | null {
    return snapshot.prep.find((flag) => flag.item === item)?.completedAt ?? null;
}

/** The shape every row derivation returns, before the shared plumbing is added. */
interface RowFacts {
    state: RowState;
    detail: string;
    note?: string | null;
    meta?: RowMeta;
}

function done(detail: string, doneDate?: string | null): RowFacts {
    return { state: 'done', detail, meta: doneDate ? { doneDate } : {} };
}

function yourMove(detail: string, note?: string): RowFacts {
    return { state: 'your-move', detail, note: note ?? null };
}

/** A record row: settled either way, with `recorded` carrying which. */
function record(recorded: boolean, detail: string, note?: string): RowFacts {
    return { state: 'done', detail, note: note ?? null, meta: { recorded } };
}

// ─── The eight sentences the rows speak ──────────────────────────────────────

const CANNOT_SEND = 'The agreement cannot be sent without this.';

const PREMIUM_CONSEQUENCE =
    'Until the premium is recorded, its bank transaction stays in the Unassigned queue forever.';

// ─── Per-row derivation ──────────────────────────────────────────────────────

/**
 * The ceremony half of an agreement row, given the server gate says "not yet".
 *
 * WHOSE MOVE IT IS DEPENDS ON WHICH SEAT IS OPEN. Primelane always countersigns last, so a
 * document whose only outstanding party is `COMPANY_REP` is not a wait at all — it is the
 * admin's own move, and calling it "waiting" is how a board sits untouched for days.
 */
function agreementCeremony(
    slot: AgreementFact | null,
    snapshot: SetupSnapshot,
    now: Date,
    noun: string,
): RowFacts {
    const document = slot?.document ?? null;

    if (!document) {
        return yourMove('Not generated', `Generate the ${noun} to start the signing.`);
    }

    if (document.status === 'VOIDED' || document.status === 'SUPERSEDED') {
        return yourMove('Voided', `The ${noun} was voided. Generate it again.`);
    }

    const signed = signedCount(document);
    const total = document.signatures.length;

    if (document.status === 'COMPLETED' && !document.finalizedAt) {
        const startedAt = newestSignedAt(document);
        const elapsed = startedAt ? Math.max(0, differenceInSeconds(now, asDate(startedAt) as Date)) : 0;
        return {
            state: 'waiting',
            detail: `sealing · ${elapsed}s`,
            note: 'Signed by every party · sealing the executed PDF — this finishes on its own.',
            meta: { sealElapsedSeconds: elapsed, actor: 'Primelane' },
        };
    }

    const pending = pendingSignature(document);
    if (!pending) {
        return { state: 'waiting', detail: `${signed} of ${total} signed`, note: null, meta: {} };
    }

    const who = partyName(pending.party, snapshot);
    if (pending.party === 'COMPANY_REP') {
        // NOT OUR MOVE WHILE ANOTHER INSTRUMENT IS OUTSTANDING [D-B18, D-B27], and it carries no
        // `actor`: the wait belongs to the sibling row, which names its own person and its own
        // document — which is the row the narrator should be speaking about.
        const blocked = countersignBlockedNote(slot, snapshot);
        if (blocked) {
            return { state: 'waiting', detail: `${signed} of ${total} signed`, note: blocked, meta: {} };
        }
        return yourMove(`${signed} of ${total} signed`, 'Every other party has signed. Countersign as Primelane.');
    }

    const token = tokenFor(slot, pending.party);
    const sentAt = token?.sentAt ?? null;
    // THE SAME CALENDAR `whenText` SPEAKS IN, because they are printed in one breath: the note says
    // "sent Friday" and `StatsLedger` prints this number as `3d` beside it. Off host-zone Dates the
    // two disagreed by a day east of Greenwich.
    const sentDay = businessDay(sentAt);
    const today = businessDay(now);
    const elapsedDays = sentDay && today ? Math.max(0, differenceInCalendarDays(today, sentDay)) : undefined;
    const opened = token?.openCount ?? 0;

    const sent = sentAt ? ` — sent ${whenText(sentAt, now)}` : '';
    const openedText = sentAt
        ? opened > 0
            ? `, opened ${opened === 1 ? 'once' : opened === 2 ? 'twice' : `${opened} times`}`
            : ', not yet opened'
        : '';

    return {
        state: 'waiting',
        detail: `${signed} of ${total} signed`,
        note: `Waiting on ${who} to sign${sent}${openedText}.`,
        meta: { actor: who, elapsedDays, sentAt: sentAt ?? undefined, openCount: opened },
    };
}

/** The hire purchase agreement — the master instrument, and a server gate. */
function hpaRow(snapshot: SetupSnapshot, now: Date): RowFacts {
    const slot = slotOf(snapshot, 'HIRE_PURCHASE');

    if (isSealedForActivation(slot)) {
        const executed = dateText(slot!.legacySignedAt);
        const sealing = slot!.document?.status === 'COMPLETED' && !slot!.document.finalizedAt;
        return {
            state: 'done',
            detail: executed ? `Executed ${executed}` : 'Executed',
            note: sealing ? 'Executed · the signed PDF is still being filed.' : null,
            meta: { doneDate: executed },
        };
    }

    return agreementCeremony(slot, snapshot, now, 'hire purchase agreement');
}

/**
 * The Vehicle Schedule — the partner's instrument, and the hire purchase agreement's precondition
 * [D-B48].
 *
 * IT IS THE FIRST SIGNATURE ON A PARTNER'S CAR AND THE ONLY ONE PRIMELANE DOES NOT CONTROL THE
 * TIMING OF. `agreement_sign` refuses the DRIVER seat on the hire purchase agreement with
 * `AGREEMENT_PRIOR_SIGNATURE_REQUIRED` while the vehicle's current partner has not executed a
 * schedule about the vehicle this contract names, at the fee this contract now charges — so on a
 * third-party vehicle the whole ceremony downstream of this row is shut until it is signed.
 *
 * THE HOUSE ARM IS DORMANCY, NOT COMPLETION, and all three parts of the grey mark are set for it
 * (`record(false, …)` here, `record` from `rowClassOf`, `true` from `rowOptionalOf`): nothing is
 * owed on one of Primelane's own vehicles, the server emits no blocker over it, and a row drawn
 * `done` with a green tick would be claiming a document that will never exist. The detail names
 * the SHAPE OF THE CONTRACT rather than an outstanding thing, exactly as the deposit's two rows do
 * on a contract that took no deposit [D-B41].
 *
 * ASKED OF THE SNAPSHOT AND NOT OF THE GATES OBJECT, because `rowFacts` is given the row and the
 * snapshot and nothing else — and the two answers cannot drift: `partnerScheduleRequired` below is
 * the same predicate over the same field, called once here and once in `computeSetupState`.
 */
function partnerScheduleRow(snapshot: SetupSnapshot, now: Date): RowFacts {
    /*
     * TWO CAUSES, TWO SENTENCES. `isThirdPartyVehicle` is false for the house AND for a null — and
     * a null here is a contract that names no vehicle at all, which is where every contract starts.
     * One sentence for both told an admin who had not reached the vehicle step yet that Primelane
     * owns a car they have not chosen. The row is dormant either way; only the detail differs.
     */
    if (snapshot.vehicle.partnerId === null || snapshot.vehicle.partnerId === undefined) {
        return record(false, 'No vehicle on this contract yet');
    }
    if (!isThirdPartyVehicle(snapshot.vehicle.partnerId)) {
        return record(false, 'Primelane-owned vehicle');
    }

    const slot = slotOf(snapshot, 'PARTNER_MANAGEMENT');

    if (isSealedForActivation(slot)) {
        const executed = dateText(slot!.legacySignedAt);
        const sealing = slot!.document?.status === 'COMPLETED' && !slot!.document.finalizedAt;
        return {
            state: 'done',
            detail: executed ? `Executed ${executed}` : 'Executed',
            note: sealing ? 'Executed · the signed PDF is still being filed.' : null,
            meta: { doneDate: executed },
        };
    }

    return agreementCeremony(slot, snapshot, now, 'vehicle schedule');
}

/**
 * The witness agreement — drawn as the hire purchase agreement's dependent [D-B18].
 *
 * THE DEPENDENCY IS THE DRIVER'S SIGNATURE, NOT THE HIRE PURCHASE AGREEMENT'S EXECUTION. That is
 * the whole of what R1 rules and the whole of what the server enforces: `agreement_issue_token`
 * withholds the witness's link, and `agreement_sign` refuses an in-person witness capture, until
 * the driver has signed — either on the live document's DRIVER seat or on the legacy row a paper
 * original was typed onto. Waiting for the document to be EXECUTED would be circular now: the
 * hire purchase agreement cannot be countersigned until the witness agreement is signed.
 *
 * AND THE `missing[]` BRANCH IS GONE WITH IT. It read `contract.hire_purchase_signed_date` off the
 * witness document's own pre-flight, which resolved only at the hire purchase agreement's finalize;
 * since [D-B18]'s resolver falls back to the DRIVER seat, that field is filled the moment the
 * driver signs — the same instant this row unlocks — so the branch could only ever contradict the
 * row it was meant to agree with.
 */
function witnessRow(snapshot: SetupSnapshot, now: Date): RowFacts {
    const slot = slotOf(snapshot, 'WITNESS');

    if (isSealedForActivation(slot)) {
        const executed = dateText(slot!.legacySignedAt);
        return {
            state: 'done',
            detail: executed ? `Executed ${executed}` : 'Executed',
            meta: { doneDate: executed },
        };
    }

    /**
     * THE LOCK IS ABOUT STARTING, AND ONLY ABOUT STARTING. Once the witness has actually signed,
     * this row is a ceremony and the ceremony speaks for it — including the countersign refusal,
     * which is the same fact worded for the seat it is holding up. Re-locking a document a party
     * has already signed would erase their signature from the row, and that shape is real: voiding
     * and reissuing the hire purchase agreement takes the driver's signature away again (W8).
     */
    const witnessSeatSigned = (slot?.document?.signatures ?? []).some(
        (signature) => signature.party === 'WITNESS' && SIGNED_STATUSES.has(signature.status),
    );

    if (!driverSignedHirePurchase(snapshot) && !witnessSeatSigned) {
        const unlockText = 'Unlocks when the driver signs the hire purchase agreement.';
        return { state: 'locked', detail: 'Locked', note: unlockText, meta: { unlockText } };
    }

    if (!slot?.document) {
        const who = snapshot.witness?.name || 'the witness';
        return yourMove('Unlocked', `The driver has signed — ${who} can sign the witness agreement now.`);
    }
    return agreementCeremony(slot, snapshot, now, 'witness agreement');
}

/**
 * The deposit agreement — a gate wherever a deposit is linked [D-B15].
 *
 * THE DERIVATION IS UNCHANGED AND STILL RECORD-SHAPED. What moved is the row's CLASS, which
 * `rowClassOf` decides from the contract rather than from this function: the same states an
 * optional record drew are what a required gate draws, and `computeSetupState` is where the
 * server's verdict — or, without one, the seal test — is applied on top.
 */
function depositAgreementRow(snapshot: SetupSnapshot, now: Date): RowFacts {
    const slots = slotsOf(snapshot, 'DEPOSIT');
    if (slots.length === 0 || snapshot.deposits.length === 0) {
        return record(false, 'Not on file');
    }

    const outstanding = slots.find((slot) => !isSealedForActivation(slot) && slot.document);
    if (outstanding) {
        const ceremony = agreementCeremony(outstanding, snapshot, now, 'deposit agreement');
        // A record row never says "your move" about itself; a signature it is waiting on is a
        // fact about somebody else, and that one it may say.
        if (ceremony.state === 'waiting') return ceremony;
        return record(false, ceremony.detail, ceremony.note ?? undefined);
    }

    const sealed = slots.filter((slot) => isSealedForActivation(slot));
    // THE DENOMINATOR IS THE DEPOSITS, AND IT IS ALREADY THE SAME NUMBER AS THE SLOTS. One DEPOSIT
    // slot is built per `contract_deposits` row (`boardSlots`, `input.deposits.forEach`) off
    // `depositSubjectsFor` — a 1:1 map over the very array this snapshot's `deposits` is built
    // from, both taken from `useContractDeposits` — so `slots.length === snapshot.deposits.length`
    // by construction and neither reading is the buggy one. Counting the deposits is robustness
    // against a transient split (one cache answered, the other still in flight) and it names the
    // side [D-B15] grades: one signed agreement per deposit TAKEN, not per document generated.
    // THE ACTUAL BEHAVIOUR CHANGE IS THE BRANCH BELOW — a contract that has papered some of its
    // deposits reads `N of M executed` instead of falling through to a flat `Not on file`.
    const owed = snapshot.deposits.length;
    if (sealed.length >= owed) {
        const executed = dateText(sealed[0].legacySignedAt);
        return {
            state: 'done',
            detail: executed ? `Executed ${executed}` : 'Executed',
            meta: { recorded: true, doneDate: executed },
        };
    }
    if (sealed.length > 0) return record(false, `${sealed.length} of ${owed} executed`);

    return record(false, 'Not on file');
}

/**
 * The insurance loan agreement — a gate wherever a loan is linked [D-B14], drawn on the insurance
 * area beside the loan it papers.
 *
 * THE DERIVATION IS RECORD-SHAPED AND THE CLASS IS NOT, exactly as the deposit agreement's is:
 * `rowClassOf` reads the contract, and `computeSetupState` applies the server's verdict — or, with
 * no payload, the seal test — on top of what this returns.
 *
 * AND IT LOCKS ON THE DRIVER'S SIGNATURE, EXACTLY AS THE WITNESS ROW DOES (owner's ruling,
 * 2026-08-18). The advance is made in connection with the hire purchase agreement and quotes its
 * reference, so it is not started before that agreement is signed — the same dependency, the same
 * predicate (`driverSignedHirePurchase`, which a paper original satisfies) and the same sentence the
 * witness row prints, because an admin reading down the rail is reading one rule twice.
 */
function insuranceLoanAgreementRow(snapshot: SetupSnapshot, now: Date): RowFacts {
    const slots = slotsOf(snapshot, 'INSURANCE_LOAN');
    if (slots.length === 0) return record(false, 'Not on file');

    /**
     * THE LOCK IS ABOUT STARTING, AND ONLY ABOUT STARTING — `witnessRow`'s rule, on the other
     * satellite. An advance that already carries a signature of its own (a signed counterparty seat,
     * or a paper original on its legacy row) is past the gate whatever the master instrument is
     * doing: re-locking a document a party has signed would erase their signature from the row, and
     * voiding and reissuing the hire purchase agreement is exactly the shape that would do it.
     */
    const advanceStarted = slots.some(
        (slot) =>
            !!slot.legacySignedAt ||
            (slot.document?.signatures ?? []).some(
                (signature) =>
                    signature.party !== 'COMPANY_REP' && SIGNED_STATUSES.has(signature.status),
            ),
    );

    if (!driverSignedHirePurchase(snapshot) && !advanceStarted) {
        const unlockText = 'Unlocks when the driver signs the hire purchase agreement.';
        return { state: 'locked', detail: 'Locked', note: unlockText, meta: { unlockText } };
    }

    const outstanding = slots.find((slot) => !isSealedForActivation(slot) && slot.document);
    if (outstanding) {
        // THE NOUN IS THE SLOT'S OWN [D-B45], never the doc type's. One type, two published
        // instruments: a ceremony sentence that said "the insurance premium advance" over an
        // Insurance Premium Payment Record would name a document that is not on this contract.
        const ceremony = agreementCeremony(
            outstanding,
            snapshot,
            now,
            INSURANCE_INSTRUMENT_NOUNS[outstanding.instrument ?? 'ADVANCE'],
        );
        if (ceremony.state === 'waiting') return ceremony;
        return record(false, ceremony.detail, ceremony.note ?? undefined);
    }

    const sealed = slots.filter((slot) => isSealedForActivation(slot));
    if (sealed.length === slots.length) {
        const executed = dateText(sealed[0].legacySignedAt);
        return {
            state: 'done',
            detail: executed ? `Executed ${executed}` : 'Executed',
            meta: { recorded: true, doneDate: executed },
        };
    }
    return record(false, 'Not on file');
}

function prepRow(snapshot: SetupSnapshot, item: string, missingNote: string): RowFacts {
    if (prepComplete(snapshot, item)) {
        const when = dateText(prepDate(snapshot, item));
        return done(when ? `Recorded ${when}` : 'Recorded', when || null);
    }
    return yourMove('—', missingNote);
}

const REQUIRED_TO_ACTIVATE = 'Required to activate the contract.';

/**
 * [D-B14], in the row's own voice — used only where the contract carries an insurance loan.
 *
 * The conditional is the whole point: this agreement is not paperwork that is always owed and
 * sometimes skipped, it is paperwork that exists exactly when there is a second debt to paper.
 */
const REQUIRED_WHEN_FINANCED =
    'Required to activate the contract when the premium is financed.';

/**
 * THE SAME RULE OVER A PREMIUM THE DRIVER PAID IN FULL [D-B45] — because "when the premium is
 * financed" is false of that year, and this sentence is the one an admin is asked to act on.
 *
 * D-B14 IS UNCHANGED AND STILL BINDING: `sign_contract` reads the insurance-loan arc on every
 * contract carrying an `insurance_loans` row, and a fully-deposited row is one. What moved is only
 * WHICH document satisfies it — the Insurance Premium Payment Record rather than the Advance — so
 * the sentence names the paperwork without claiming a debt nobody owes.
 */
const REQUIRED_WHEN_PAID_IN_FULL =
    'Required to activate the contract. A premium the driver paid in full is recorded on its own ' +
    'signed document.';

/** [D-B14]'s sentence for whichever of the two instruments this contract's premium year is on. */
function requiredWhenRecorded(instrument: InsuranceInstrument): string {
    return instrument === 'PAYMENT_RECORD' ? REQUIRED_WHEN_PAID_IN_FULL : REQUIRED_WHEN_FINANCED;
}

/**
 * [D-B15], in the row's own voice — used only where the contract carries a deposit.
 *
 * EVERY deposit, said in the row rather than left to the count: the rule the server applies is one
 * signed agreement per `contract_deposits` row, and a sentence that said "the deposit agreement"
 * would read as satisfied on the contract that papered one of its two payments.
 */
const REQUIRED_ONCE_DEPOSITED =
    'Required to activate the contract. Every deposit taken has to be papered.';

/**
 * WHAT THE DEPOSIT'S PAPER SAYS BEFORE THE MONEY IS LINKED — the conditional form, restored by
 * [D-B41].
 *
 * "BECOMES REQUIRED", BECAUSE NOW IT GENUINELY DOES. This sentence was retired on 2026-08-04 for
 * being false: `sign_contract` refused a contract with no deposit at all, so the chain — link the
 * money, paper it — was owed end to end and calling the second half contingent read as permission
 * to skip work the admin already had. A deposit is not required to activate any more, so nothing on
 * this row is owed until a payment exists; [D-B15] lands the moment one does, which is exactly what
 * a conditional says.
 */
const REQUIRED_WITH_THE_DEPOSIT =
    'Becomes required once a deposit is recorded. Every deposit taken has to be papered.';

/**
 * WHAT THE PAYMENT ROW SAYS ON A CONTRACT THAT TOOK NO DEPOSIT [D-B41].
 *
 * IT REPORTS, IT DOES NOT ASK. `agreement_merge_data` resolves `contract.deposit_amount` to '0.00'
 * where the contract carries no `contract_deposits` row, so the Schedule renders with a credited
 * deposit of nothing and `sign_contract` no longer refuses — the sentence names both facts and
 * asks for neither, because a row an admin cannot act on may not read like a task.
 *
 * `GHS`, NOT `GH₵`: this quotes the figure as the INSTRUMENT states it, and the Schedule's money
 * particulars print the currency code.
 */
const NO_DEPOSIT_TAKEN = 'No deposit was taken on this contract. The Schedule credits GHS 0.00.';

/**
 * WHAT THE LOAN'S PAPER SAYS BEFORE THE LOAN IS RECORDED — owner's ruling, 2026-08-04.
 *
 * NOT "becomes required once an insurance loan is recorded", WHICH IS THE SENTENCE THIS REPLACES,
 * and the deposit's sibling above is the pattern it is mirroring exactly. The old sentence made the
 * row read as somebody else's contingency: dormant grey, out of the denominator, waiting for a loan
 * to appear. But the row is drawn on a contract whose premium is not yet recorded either, and once
 * that premium is recorded `sign_contract` refuses the contract until the document over it is
 * sealed [D-B14] — so the chain is the admin's from end to end. The row is counted, it wears the
 * your-move dot, and its note names the order the chain is done in.
 *
 * IT NAMES BOTH OUTCOMES BECAUSE NOTHING HERE CAN YET TELL THEM APART [D-B45]. This is the
 * subject-less case — no `insurance_loans` row exists, so neither limb of `instrumentFor` has an
 * answer — and the year gets papered either way: an Insurance Premium Advance where Primelane
 * financed the premium, an Insurance Premium Payment Record where the driver paid it in full. A
 * sentence naming only the advance would tell an admin recording a paid premium that this row is
 * somebody else's.
 */
const REQUIRED_WITH_THE_LOAN =
    'Required to activate the contract. The premium above has to be recorded first, and a recorded ' +
    'premium carries a signed document either way — an advance where Primelane financed it, a ' +
    'payment record where the driver paid it in full.';

/**
 * WHAT THE VEHICLE SCHEDULE SAYS AGAINST A SERVER THAT CANNOT ANSWER [D-B48].
 *
 * The fallback's sentence, and it names the RULE rather than the refusal, because it is spoken
 * exactly where there is no refusal to quote: `get_contract_setup_status` has not answered, so the
 * page is falling to `sign_contract`'s own seal test and has to say why the row is not finished.
 * It states the ordering as well as the requirement — the partner's signature is not merely owed,
 * it is owed FIRST, and an admin who does not know that will spend the afternoon on a hire purchase
 * agreement whose driver seat the server will refuse.
 */
const REQUIRED_ON_A_PARTNER_VEHICLE =
    'Required to activate the contract. The vehicle belongs to a partner, and they sign the vehicle ' +
    'schedule before the driver can sign the hire purchase agreement.';

function rowFacts(def: RowDef, snapshot: SetupSnapshot, now: Date): RowFacts {
    switch (def.id) {
        case 'dep-payment': {
            const deposits = snapshot.deposits;
            // NO DEPOSIT IS NOT A MISSING DEPOSIT [D-B41], AND THE TWO USED TO SHARE THIS ARM. The
            // merge CASE that returns NULL is keyed on the SUM; the ruling gave the no-row case its
            // own resolution of '0.00', so the Schedule renders, the send is not blocked and
            // `sign_contract` does not refuse. Nothing is owed here, which is what `rowOptionalOf`
            // greys the row out to say — and a record's `done` is what keeps it off the numerator.
            if (deposits.length === 0) return record(false, 'None taken', NO_DEPOSIT_TAKEN);

            const total = deposits.reduce((sum, deposit) => sum + (deposit.amount ?? 0), 0);
            // A LINKED ZERO IS NOT A DEPOSIT AND IT IS NOT AN ABSENT ONE EITHER — it is a data
            // error, and this arm is unchanged by [D-B41] on purpose: `agreement_merge_data` still
            // returns NULL for rows that sum to zero, so the required merge field is still
            // unresolved and the agreement still cannot be sent. Counting it as done is how the
            // manifest would promise a send the server then refuses.
            if (total <= 0) {
                return yourMove(
                    `${money(total)} linked`,
                    `${CANNOT_SEND} The hire purchase schedule credits the deposit in its money particulars.`,
                );
            }
            const paidOn = dateText(deposits[0].paidOn);
            const detail =
                deposits.length === 1
                    ? [money(total), paidOn].filter(Boolean).join(' · ')
                    : `${money(total)} · ${deposits.length} payments`;
            return done(detail, deposits.length === 1 ? paidOn : null);
        }

        case 'dep-agreement':
            return depositAgreementRow(snapshot, now);

        case 'veh-assign': {
            const { make, model, registration, id } = snapshot.vehicle;
            if (!id || !isFilled(model)) {
                return yourMove('No vehicle', `${CANNOT_SEND} The agreement names the car it is written over.`);
            }
            const name = [make, model].filter(Boolean).join(' ');
            return done([name, registration].filter(Boolean).join(' · '));
        }

        case 'veh-service':
            return prepRow(snapshot, 'service', REQUIRED_TO_ACTIVATE);

        case 'veh-registration':
            return prepRow(
                snapshot,
                'registration',
                `${REQUIRED_TO_ACTIVATE} The plate also prints on the hire purchase agreement.`,
            );

        case 'veh-roadworthiness':
            return prepRow(
                snapshot,
                'roadworthiness',
                `${REQUIRED_TO_ACTIVATE} Most contracts reach this point without one — an empty row here is normal.`,
            );

        case 'veh-tracker':
            return prepRow(snapshot, 'tracker', REQUIRED_TO_ACTIVATE);

        case 'veh-odometer': {
            if (!prepComplete(snapshot, 'mileage')) {
                // NOT "IT PRINTS ON THE AGREEMENT" — the v1 Schedule identifies the Vehicle by make
                // and model, year, colour, registration and condition, and carries no odometer row
                // at all. The reading is an operational record: it is what a later argument about
                // wear, mileage or valuation is settled against, which is why it is still a gate and
                // why it has to be the reading taken at handover rather than at assignment.
                return yourMove(
                    '—',
                    `${REQUIRED_TO_ACTIVATE} Take the reading when the car is finally ready — it is the handover record both sides argue from later.`,
                );
            }
            const mileage = snapshot.vehicle.mileage;
            return done(mileage != null ? `${mileage.toLocaleString('en-GH')} km` : 'Recorded');
        }

        case 'drv-name': {
            const name = snapshot.driver.fullName;
            if (!isFilled(name)) {
                return yourMove('Missing', `${CANNOT_SEND} The name prints twice on the hire purchase agreement.`);
            }
            return done(name as string);
        }

        case 'drv-contact': {
            const { phone, address } = snapshot.driver;
            const missing = [!isFilled(phone) && 'Phone', !isFilled(address) && 'Address'].filter(
                Boolean,
            ) as string[];
            if (missing.length > 0) {
                return yourMove(
                    `${missing.join(' and ')} missing`,
                    `${CANNOT_SEND} The address prints on the hire purchase agreement.`,
                );
            }
            return done('Phone and address');
        }

        case 'drv-file': {
            const { licenceOnFile, nationalIdOnFile, birthDate, photo, email, repaymentTag } = snapshot.driver;
            const held = [
                licenceOnFile,
                nationalIdOnFile,
                isFilled(birthDate),
                isFilled(photo),
                isFilled(email),
                isFilled(repaymentTag),
            ].filter(Boolean).length;
            return record(held > 0, `${held} of 6 recorded`);
        }

        case 'ins-policy': {
            if (!prepComplete(snapshot, 'insurance')) {
                return yourMove('Not on cover', REQUIRED_TO_ACTIVATE);
            }
            const boundOn = dateText(snapshot.insurance?.policyStartsAt);
            return done(boundOn ? `Bound ${boundOn}` : 'On cover', boundOn || null);
        }

        case 'ins-premium': {
            const premium = snapshot.insurance?.premium ?? null;
            if (!premium || premium.amount == null) return record(false, 'Not recorded', PREMIUM_CONSEQUENCE);
            const paidOn = dateText(premium.paidOn);
            return record(true, [money(premium.amount), paidOn].filter(Boolean).join(' · '));
        }

        case 'ins-loan': {
            const loan = snapshot.insurance?.loan ?? null;
            if (!loan || loan.amount == null) return record(false, 'Not financed');
            /**
             * A PREMIUM THE DRIVER PAID IN FULL IS NOT A FINANCED ONE, AND IT PRINTS NO RATE
             * [D-B45]. The deposit covers the whole amount, so the interest base
             * (`amount − deposits`) is zero and the stored rate — 0, NULL or a stale 43 — accrues
             * nothing whatever it says. Printing " at 43%" over a year nobody was charged for is
             * the one figure on this rail an admin would act on and shouldn't.
             *
             * THE SHAPE IS THE PREMIUM ROW'S, deliberately: money · the day it was paid. The two
             * rows sit one above the other and describe the same premium from the two sides of it —
             * what Primelane paid the insurer, and what the driver paid Primelane.
             */
            if (insuranceInstrumentOf(snapshot) === 'PAYMENT_RECORD') {
                const paidOn = dateText(loan.depositPaidOn);
                return record(true, [money(loan.amount), paidOn].filter(Boolean).join(' · '));
            }
            const rate = loan.rate == null ? '' : ` at ${loan.rate}%`;
            return record(true, `${money(loan.amount)}${rate}`);
        }

        case 'ins-loan-agreement':
            return insuranceLoanAgreementRow(snapshot, now);

        case 'fin-cash-price': {
            /**
             * TYPED, NEVER DERIVED, so an empty column is a fact rather than a figure that has not
             * been worked out yet: `agreement_merge_data` reads `contracts.cash_price` straight and
             * resolves NULL where it is unset rather than falling back to `sold_at`, because two
             * equal prices on a signed page state that Primelane charged nothing for the credit.
             * The canvas explains that beside the field; the row states the consequence.
             */
            const cashPrice = snapshot.financials.cashPrice;
            if (cashPrice == null) {
                return yourMove(
                    '—',
                    `${CANNOT_SEND} The cash price prints as the Cash price, and the hire purchase ` +
                    'agreement cannot even be generated without it.',
                );
            }
            return done(money(cashPrice));
        }

        case 'fin-price': {
            const price = snapshot.financials.sellingPrice;
            if (price == null) {
                return yourMove(
                    '—',
                    `${CANNOT_SEND} The selling price prints as the Vehicle Purchase Price.`,
                );
            }
            return done(money(price));
        }

        case 'fin-instalment': {
            const instalment = snapshot.financials.instalment;
            if (!instalment || instalment.amount == null || !isFilled(instalment.startsAt)) {
                return yourMove(
                    '—',
                    `${CANNOT_SEND} The weekly instalment prints as the Weekly Instalment, and the Term is derived from it.`,
                );
            }
            const from = dateText(instalment.startsAt);
            return done([money(instalment.amount), from].filter(Boolean).join(' · '));
        }

        case 'agr-parties': {
            const witness = snapshot.witness;
            const missing: string[] = [];
            if (!isFilled(snapshot.driver.fullName)) missing.push('Driver');
            if (!witness?.id || !isFilled(witness.name)) missing.push('Witness');
            else {
                if (!isFilled(witness.phone)) missing.push('Witness phone');
                if (!isFilled(witness.address)) missing.push('Witness address');
            }
            if (missing.length > 0) {
                return yourMove(
                    `${missing.join(', ')} missing`,
                    `${CANNOT_SEND} The witness agreement prints the witness with a phone and an ` +
                    'address; the hire purchase agreement prints neither.',
                );
            }
            // TWO, NOT THREE. Primelane is a party to both instruments and is resolved by nobody —
            // counting the company among the things an admin has to go and fill in was how this row
            // read "3 parties resolved" while only two people had ever been chosen.
            return done('2 parties resolved');
        }

        case 'agr-partner':
            return partnerScheduleRow(snapshot, now);

        case 'agr-hpa':
            return hpaRow(snapshot, now);

        case 'agr-witness':
            return witnessRow(snapshot, now);

        default:
            return yourMove('—');
    }
}

// ─── Reading the server's answer ─────────────────────────────────────────────

/** The codes standing against this contract right now, or null when the server has not answered. */
function blockerCodes(snapshot: SetupSnapshot): Set<string> | null {
    const status = snapshot.serverStatus;
    if (!status || !Array.isArray(status.blockers)) return null;
    return new Set(status.blockers.map((blocker) => blocker.code));
}

/**
 * THE SERVER'S BLOCKER LIST, VERBATIM — EXCEPT THE ONE ARM THE CLIENT CAN SAY PRECISELY.
 *
 * `DRIVER_PROFILE_INCOMPLETE` is the only refusal whose message is a FIXED enumeration: the server
 * emits "Complete the driver's profile: first and last name, date of birth, phone and address." on
 * every contract it holds, so an admin whose driver was missing nothing but a date of birth was
 * sent to four fields, three of which were already right. The server sends the CODE; the fields it
 * graded are on this page already — the same row under the same `admin_id IS NULL` exclusion, via
 * `driverProfileOf` — so this is the only layer where the two can be put together.
 *
 * REWRITTEN HERE, AT THE ONE SEAM, AND NOT AT THE THREE SURFACES THAT READ IT. The rail hint, the
 * pinned red footer and the confirm dialog's list all take their words from this list (through
 * `activationVerdict` and `landRefusals`); narrowing the sentence per surface is how this page
 * ended up with two Activate controls that disagreed, and the fix for that was to make the shared
 * answer the only answer. Every other message passes through untouched — the object is copied, not
 * mutated, so the payload the query cache holds stays the server's.
 *
 * TWO GUARDS, BOTH FAILING TOWARD THE SERVER'S OWN WORDING:
 *   · no driver on the client's page — the server would be refusing with `DRIVER_NOT_ASSIGNED`, so
 *     this code against a driverless snapshot is drift (or a payload that landed before the
 *     contract row did), and a list of five "missing" fields would be a page talking about a driver
 *     it does not have;
 *   · nothing visibly absent — `driverProfileGapSentence` answers null, and the generic sentence
 *     stands. Never claim completeness against a live refusal.
 */
function spokenBlockers(snapshot: SetupSnapshot): SetupStatusBlocker[] {
    const raw = snapshot.serverStatus?.blockers;
    if (!Array.isArray(raw)) return [];
    const specific =
        snapshot.driver.id === null ? null : driverProfileGapSentence(snapshot.driver);
    if (!specific) return raw;
    return raw.map((blocker) =>
        blocker.code === DRIVER_PROFILE_INCOMPLETE ? { ...blocker, message: specific } : blocker,
    );
}

function sectionOf(snapshot: SetupSnapshot, key: SetupSectionKey): SetupStatusSection | null {
    const sections = snapshot.serverStatus?.sections;
    if (!Array.isArray(sections)) return null;
    return sections.find((section) => section.key === key) ?? null;
}

/** Every code the server is holding against one row. At most one per row, by construction. */
function codesForRow(codes: Set<string> | null, rowId: ManifestRowId): string[] {
    if (!codes) return [];
    return [...codes].filter((code) => BLOCKER_ROWS[code] === rowId);
}

/**
 * Does this contract carry an insurance loan? [D-B14] turns on this and nothing else.
 *
 * READ FROM ARITHMETIC ON BOTH SIDES, NEVER FROM A BLOCKER: the agreements section budgets THREE
 * required items when there is a loan and TWO when there is not, so `required_total` answers the
 * question without depending on whether anything is outstanding. A sealed loan agreement emits no
 * code at all, and a rule keyed to a code would read that as "no loan" and quietly drop the row
 * back to dormant the moment it was satisfied.
 */
function insuranceLoanPresent(snapshot: SetupSnapshot): boolean {
    // THE ROW'S EXISTENCE, NOT ITS AMOUNT — `v_loan_present` is `il.id IS NOT NULL`, and a loan
    // recorded with its amount still blank is a loan the driver owes under an unsigned instrument.
    //
    // LOCAL EVIDENCE IS CHECKED FIRST AND THE TWO ARE OR-ed, never short-circuited on the payload
    // alone: a loan the client can SEE is a loan, whatever the section budget says. The arithmetic
    // below is a property of the DEPLOYED function, and this client runs against servers that
    // predate it — production carries no `get_contract_setup_status` at all today. Reading the
    // section as authoritative would let an older payload answer "no loan" for a loan sitting on
    // screen, and the row would quietly fall back to its dormant presentation.
    if (snapshot.insurance?.loan) return true;
    const agreements = sectionOf(snapshot, 'agreements');
    return agreements ? agreements.required_total >= 3 : false;
}

/**
 * Does this contract carry a deposit? [D-B15] AND [D-B41] both turn on this and nothing else.
 *
 * THREE ARMS, BECAUSE TWO SERVER BUDGETS ARE LIVE AT ONCE. [D-B41] removed the deposit arm, so the
 * deposits section now budgets `n` items where it budgeted `1 + n` before — and this client runs
 * against both bodies across a deploy window, so a rule written for one of them answers the
 * question backwards for the whole of the other. The arms, in order:
 *
 *   1. `deposits.length > 0`   a deposit the CLIENT CAN SEE is a deposit, and no server answer can
 *                              make it not one — `snapshot.deposits` is a direct read of
 *                              `contract_deposits` for this contract. It is also the only arm that
 *                              answers at all against production, which carries no
 *                              `get_contract_setup_status`.
 *   2. `DEPOSIT_MISSING` filed only the PRE-[D-B41] body emits it, and only at n = 0 — which is the
 *                              one case the arithmetic below cannot tell apart.
 *   3. `required_total >= 1`   n ≥ 1 under either budget.
 *
 * THE FOUR CASES, WORKED, because the middle arm looks redundant until they are: old body n = 0 →
 * total 1 with the code filed → FALSE (arm 3 alone would have said true, and the rows would have
 * stayed lit under a live refusal); old body n ≥ 1 → total 1 + n, no code → TRUE; new body n = 0 →
 * total 0, no code → FALSE; new body n ≥ 1 → total n, no code → TRUE. Older still — the pre-[D-B15]
 * budget, a flat 1 — resolves the same way: the code at n = 0, arm 3 above it.
 *
 * NEVER KEYED TO THE PAPER ARMS. A sealed deposit agreement emits no code at all, so a rule that
 * read `AGREEMENT_DEPOSIT_*` would drop the rows back to dormant at the moment they had been
 * earned. `required_total` is arithmetic on both sides and answers whether anything is outstanding
 * or not.
 *
 * THE ROW'S EXISTENCE, NOT ITS AMOUNT: a deposit refunded down to nothing was still taken and still
 * has to be papered [D-B11].
 */
function depositPresent(snapshot: SetupSnapshot): boolean {
    if (snapshot.deposits.length > 0) return true;
    const deposits = sectionOf(snapshot, 'deposits');
    if (!deposits) return false;
    if (deposits.blockers.includes(DEPOSIT_MISSING)) return false;
    return deposits.required_total >= 1;
}

/**
 * Does this contract owe a Vehicle Schedule? [D-B48] turns on this and nothing else.
 *
 * LOCAL EVIDENCE ONLY, AND NO `required_total` ARM — the one conditional gate on this page derived
 * WITHOUT the section budget, which is a deliberate departure from `insuranceLoanPresent` and
 * `depositPresent` beside it rather than an oversight.
 *
 * The reason is that the two questions are not equally answerable. Whether a contract carries a
 * loan or a deposit is a fact about ROWS THIS PAGE CANNOT ALWAYS SEE, so the budget is a second
 * witness worth consulting. Who owns the vehicle is a fact about the contract row the Dossier reads
 * on every load — `contracts.vehicle_id → vehicles.partner_id` — so there is nothing for a second
 * witness to add. What it WOULD add is a failure mode: `get_contract_setup_status` bodies that
 * predate `20260908010000` budget two agreements items on a third-party contract exactly as they do
 * on a house one, so an OR over the budget answers "no partner" for every contract until the
 * migration lands, and an AND drops the row on a server that has not been upgraded yet. The vehicle
 * on the page is the same evidence the server derives its own arm from, and it is right on both
 * sides of the deploy window.
 *
 * THE SERVER STILL DECIDES WHETHER THE GATE IS MET. This predicate only decides whether the row is
 * a gate at all; `reconcileGateRow` moves it across the done line on `AGREEMENT_PARTNER_MISSING` /
 * `AGREEMENT_PARTNER_AWAITING_SEAL`, and the fallback below answers where the payload cannot.
 */
function partnerScheduleRequired(snapshot: SetupSnapshot): boolean {
    return isThirdPartyVehicle(snapshot.vehicle.partnerId);
}

/** Every deposit on this contract carries a sealed agreement — the seal test, client-side. */
function depositsSealed(snapshot: SetupSnapshot): boolean {
    const owed = snapshot.deposits.length;
    if (owed === 0) return true;
    return slotsOf(snapshot, 'DEPOSIT').filter((slot) => isSealedForActivation(slot)).length >= owed;
}

/**
 * The three rows whose CLASS is a property of the contract rather than of the row.
 *
 * [D-B14] — a contract that finances its premium owes money under a second instrument with its own
 * instalment schedule and its own repayments, and it was being activated unsigned: 170 of the 196
 * contracts carrying a loan had no sealed loan agreement.
 *
 * [D-B15] — a deposit agreement papers money the driver has already handed over, and it was
 * optional on every contract: `contract_deposit_agreements` holds zero rows against 249 deposits.
 * The owner's ruling is that it is required, and the server enforces it per deposit.
 *
 * [D-B41] — a deposit is not required to activate. `get_contract_setup_status` stopped emitting
 * `DEPOSIT_MISSING` and the Schedule credits GHS 0.00 without one, so the payment row's promise
 * ("the agreement cannot be sent without this") became a claim the server contradicts.
 *
 * THE LOAN'S PAPER IS CONDITIONAL IN CLASS AND IN CLASS ONLY. Where the loan does not exist there
 * is no document for the server to grade, so the row keeps its record class — but NOT the word
 * "optional", which never renders on it in any state, and NOT the dormant presentation either: it
 * is counted and wears the your-move dot from the first draw (owner's ruling, 2026-08-04).
 *
 * THE DEPOSIT'S TWO ARE CONDITIONAL IN PRESENTATION TOO, AND THAT IS [D-B41]'S DOING RATHER THAN A
 * SECOND OPINION ABOUT THE SAME FACTS. Nothing is owed on either row until a payment exists, so
 * `rowOptionalOf` takes both out of the counter and hands both the grey mark; the moment one is
 * linked they are a render-blocker and a gate again.
 */
interface ConditionalGates {
    loanPresent: boolean;
    depositPresent: boolean;
    /**
     * [D-B48] — the vehicle belongs to a third-party partner, so a signed Vehicle Schedule about it
     * is a condition of activation. FALSE ON ONE OF PRIMELANE'S OWN CARS, where the server emits no
     * arm at all and the row is dormant rather than merely unmet.
     */
    partnerScheduleRequired: boolean;
}

/**
 * WHAT THIS ROW IS CALLED — `def.label` for every row but the two insurance ones [D-B45].
 *
 * A ROW'S NAME IS STATIC EXCEPT WHERE THE THING IT NAMES HAS TWO NAMES. `INSURANCE_LOAN` now
 * carries two published instruments — an Insurance Premium Advance for a year Primelane financed,
 * an Insurance Premium Payment Record for a year the driver paid in full — and the rail draws the
 * label beside the detail on one line, so a row headed "Premium advance" reading "GH₵5,632 · Jan 8,
 * 2026" would be contradicting itself in six words.
 *
 * BOTH ROWS MOVE TOGETHER because they are the two halves of one year: the money row names what was
 * recorded, the paper row names the document that papers it. Every other row keeps its constant.
 */
function labelOf(def: RowDef, snapshot: SetupSnapshot): string {
    if (def.id !== 'ins-loan' && def.id !== 'ins-loan-agreement') return def.label;
    if (insuranceInstrumentOf(snapshot) !== 'PAYMENT_RECORD') return def.label;
    return def.id === 'ins-loan' ? 'Premium paid in full' : 'Insurance premium payment record';
}

function rowClassOf(def: RowDef, gates: ConditionalGates): RowClass {
    if (def.id === 'ins-loan-agreement' && gates.loanPresent) return 'gate';
    if (def.id === 'dep-agreement' && gates.depositPresent) return 'gate';
    // `record`, AND IT HAS TO BE `record` FOR THE MARK TO COME OUT GREY. `isRowSettled` returns
    // false for a record whose `meta.recorded` is false, which is what leaves `markFor` a `done`
    // row to re-mark; on any other class the row would be counted as finished work and drawn with
    // the green tick. The class is also true on its own terms — nothing renders from this row and
    // nothing is refused over it [D-B41].
    if (def.id === 'dep-payment' && !gates.depositPresent) return 'record';
    // THE SCHEDULE FALLS THE OTHER WAY FROM THE OTHER TWO AGREEMENTS [D-B48]: it is declared a gate
    // and demoted where the contract does not owe it, rather than declared a record and promoted.
    // That is the honest declaration — on a third-party vehicle it really is what `sign_contract`
    // refuses over, and third-party is the only shape that draws the row as work — but the demotion
    // is what makes the mark grey on one of Primelane's own cars, for `dep-payment`'s reason above.
    if (def.id === 'agr-partner' && !gates.partnerScheduleRequired) return 'record';
    return def.cls;
}

/**
 * IS THIS ROW DORMANT ON THIS CONTRACT — the flag `isCountedRow` and `markFor` both read.
 *
 * THE GREY MARK NEEDS ALL THREE OF ITS PARTS AND THIS IS ONLY ONE OF THEM — `optional` on its own
 * is not dormancy, which is the trap this function is written beside. `markFor` reaches `record`
 * only for a row that is `state: 'done'`, NOT settled, and `optional`: on the deposit rows that is
 * `record(false, …)` from `rowFacts`, `record` from `rowClassOf` and `true` from here. Drop any one
 * of the three and the row draws a blue dot or a green tick instead, silently.
 *
 * BESIDE `rowClassOf` AND NOT INSIDE IT, because the two answers are not the same answer: the
 * deposit's PAPER is a `record` on a contract with no deposit and also on one whose deposit is
 * unpapered under a server that cannot answer, and only the first of those is dormant.
 */
function rowOptionalOf(def: RowDef, gates: ConditionalGates): boolean {
    if ((def.id === 'dep-payment' || def.id === 'dep-agreement') && !gates.depositPresent) {
        return true;
    }
    // A SECOND CONTRACT SHAPE DECLARES DORMANCY [D-B48], and it is dormancy on the same terms the
    // deposit's is: on one of Primelane's own vehicles nothing is owed, nothing the admin types can
    // make it owed, and `get_contract_setup_status` does not emit the arm at all. It is the third
    // part of the grey mark — `record(false, 'Primelane-owned vehicle')` and `rowClassOf`'s demotion
    // are the other two, and any one of the three missing draws a blue dot instead.
    if (def.id === 'agr-partner' && !gates.partnerScheduleRequired) return true;
    return !!def.optional;
}

/**
 * The server's verdict applied to one gate row.
 *
 * THE SERVER MAY ONLY MOVE A ROW ACROSS THE done LINE, never around inside it. A row whose blocker
 * is absent is met, whatever the client derived; a row whose blocker is present is not met,
 * whatever the client derived. Everything else — whose move it is, who is being waited on, how
 * long the link has been out, what the unlock condition reads — stays the client's, because the
 * payload carries no ceremony and a row that said only "outstanding" would be a downgrade.
 *
 * The one demotion that matters is the live defect this whole chain exists to kill: a document
 * every party has signed reads `done` to the client and `_AWAITING_SEAL` to the server, and it is
 * the server that `sign_contract` agrees with.
 */
function reconcileGateRow(facts: RowFacts, code: string | undefined, message: string | undefined): RowFacts {
    if (!code) {
        if (facts.state === 'done') return facts;
        return { ...facts, state: 'done', note: null };
    }
    if (facts.state !== 'done') return facts;
    return {
        ...facts,
        state: code.endsWith('_AWAITING_SEAL') ? 'waiting' : 'your-move',
        note: message ?? facts.note ?? null,
    };
}

// ─── The whole state ─────────────────────────────────────────────────────────

/**
 * Documents that are countersigned but whose executed PDF is not filed yet.
 *
 * ACTIVATION WAITS FOR THIS AND SAYS SO. `sign_contract` reads the legacy `agreement_url`, which
 * only `agreement_finalize` writes, so between the last signature and the seal the server refuses
 * a contract every other surface calls complete. Which INSTRUMENTS that covers is the payload's
 * answer now: the loan agreement joined the set where the contract carries a loan [D-B14] and the
 * deposit agreement where it carries a deposit [D-B15]. The client's own fallback stays at the two
 * contract instruments, and deliberately — see below.
 */
function sealPendingFor(snapshot: SetupSnapshot, now: Date, codes: Set<string> | null): SealPending | null {
    /**
     * WHEN THE SERVER HAS ANSWERED, THE `_AWAITING_SEAL` CODES ARE THE LIST.
     *
     * They are the same predicate this function derived by hand — a document at `COMPLETED` whose
     * legacy row is still empty — asked of the rows `sign_contract` reads rather than of the cache,
     * so the counter and the refusal cannot come apart. The one behavioural difference is that the
     * loan agreement is now among them where the contract carries a loan [D-B14], and the deposit
     * agreement where it carries a deposit [D-B15]: both bear on activation, so a seal running on
     * either is a wait the activation panel has to show. The client's own filter stays as the
     * fallback, and it stays two-instrument, because before this payload existed neither of those
     * two genuinely bore on activation and the fallback may not invent a rule it was never given.
     */
    const awaitingTypes = codes
        ? new Set(
            [...codes]
                .map((code) => AWAITING_SEAL_DOC_TYPES[code])
                .filter((docType): docType is AgreementDocTypeKey => !!docType),
        )
        : null;

    const pending = awaitingTypes
        ? snapshot.agreements.filter((slot) => awaitingTypes.has(slot.docType))
        : snapshot.agreements.filter(
            (slot) =>
                (slot.docType === 'HIRE_PURCHASE' || slot.docType === 'WITNESS') &&
                slot.document?.status === 'COMPLETED' &&
                !slot.document.finalizedAt,
        );

    // THE COUNT IS THE SERVER'S, NOT THE CACHE'S. A slot the payload calls `_AWAITING_SEAL` that
    // this page has no cached document for is still a seal in flight — reporting zero because the
    // rows have not arrived is how the button goes green under a running finalize.
    const count = awaitingTypes ? awaitingTypes.size : pending.length;
    if (count === 0) return null;

    // THE CLOCK STARTS AT THE LAST SIGNATURE, not the first: finalization is what the completed
    // ceremony hands off to, so the elapsed figure an admin reads is the age of the seal, not the
    // age of the document.
    const signedDates = pending
        .map((slot) => newestSignedAt(slot.document))
        .filter((value): value is string => !!value)
        .sort();
    const startedAt = signedDates.length ? signedDates[signedDates.length - 1] : null;

    return {
        count,
        startedAt,
        elapsedSeconds: startedAt ? Math.max(0, differenceInSeconds(now, asDate(startedAt) as Date)) : 0,
    };
}

function receiptFor(area: SetupArea, snapshot: SetupSnapshot): string {
    const base = AREA_RECEIPTS[area];
    if (area === 'deposit') {
        // NO DEPOSIT IS ITS OWN RECEIPT [D-B41]. The group is satisfied on this contract — both
        // rows are dormant — and `Deposit received` would be a dated claim about money that never
        // arrived. This one is deliberately dateless: there is no event to date.
        if (snapshot.deposits.length === 0) return 'No deposit taken';
        const paidOn = dateText(snapshot.deposits[0]?.paidOn);
        return paidOn ? `${base} ${paidOn}` : base;
    }
    if (area === 'insurance') {
        const boundOn = dateText(snapshot.insurance?.policyStartsAt);
        return boundOn ? `${base} ${boundOn}` : base;
    }
    return base;
}

function narratorFor(
    snapshot: SetupSnapshot,
    rows: ManifestRow[],
    gate: ActivationGate,
    now: Date,
): Narrator {
    /**
     * THE BAND NEVER REPEATS THE HEADER. The header above it carries the contract number in its
     * breadcrumb and the state in its status badge, so a sentence opening "Contract #298 is
     * active" spent its whole width saying what was already on screen twice. What is left is the
     * part only the band knows — the date, or where the work has moved to — which is the one
     * thing an admin reads a narrator for.
     *
     * AND ONCE ACTIVE IT CARRIES NO ACTION AT ALL. The one action here was `Open contract`, which
     * navigated to the very place the breadcrumb above it links to — a brand-filled button whose
     * whole job was duplicating a crumb. Navigation lives in the breadcrumb; the band keeps the
     * sentence, and the only actions it ever spends brand ink on are the ones that MOVE the
     * contract: activate it, or get somebody to sign.
     */
    if (gate.activated) {
        const startsAt = dateText(snapshot.financials.instalment?.startsAt);
        return {
            sentence: startsAt
                ? `The first payment is due ${startsAt}.`
                : 'Setup is closed. Payments are tracked on the contract.',
            actions: [],
        };
    }

    if (gate.sealPending) {
        return {
            sentence:
                `Sealing the executed PDF — this finishes on its own · ${gate.sealPending.elapsedSeconds}s.`,
            actions: [],
        };
    }

    /**
     * A REFUSAL ABOUT THE CALL OUTRANKS A REFUSAL ABOUT A ROW. "Access denied" and "Contract is
     * already IN_PROGRESS" describe the attempt, not the setup, and no row will ever clear them —
     * so they are spoken verbatim with no Fix link rather than counted among things to go and fix.
     */
    const refused = gate.refusals.filter((refusal) => !!refusal.rowId);
    const unaddressed = gate.refusals.filter((refusal) => !refusal.rowId);
    if (unaddressed.length > 0) {
        return { sentence: `Activation refused. ${unaddressed[0].message}`, actions: [] };
    }
    if (refused.length > 0) {
        const count = refused.length;
        return {
            sentence:
                `Activation refused. ${count} ${plural(count, 'reason is', 'reasons are')} on the ` +
                `${plural(count, 'row', 'rows')} ${plural(count, 'it belongs', 'they belong')} to.`,
            actions: [
                {
                    id: 'show-refusal',
                    label: 'Show me',
                    kind: 'primary',
                    intent: 'jump',
                    rowId: refused[0].rowId ?? undefined,
                    area: refused[0].area ?? undefined,
                },
            ],
        };
    }

    const witness = rows.find((row) => row.id === 'agr-witness');
    if (witness && witness.state === 'your-move' && witness.detail === 'Unlocked') {
        return {
            sentence: witness.note ?? 'The witness agreement has unlocked.',
            actions: [
                {
                    id: 'open-witness',
                    label: 'Open witness agreement',
                    kind: 'primary',
                    intent: 'jump',
                    rowId: 'agr-witness',
                    area: 'agreements',
                },
            ],
        };
    }

    /**
     * A GATE AN ADMIN CANNOT MOVE IS NOT THE HEADLINE. Where every unmet gate is out for
     * signature, the useful sentence is who is being waited on — "2 gates left" reads as work
     * nobody has started, when in fact both are on somebody else's desk.
     */
    const unmet = gate.unmet;
    const actionable = unmet.find((row) => row.state === 'your-move');
    const gateSentence = (row: ManifestRow): Narrator => ({
        sentence: `${unmet.length} ${plural(unmet.length, 'gate', 'gates')} left, starting with ${row.label.toLowerCase()}.`,
        actions: [
            {
                id: 'jump-gate',
                label: 'Take me there',
                kind: 'primary',
                intent: 'jump',
                rowId: row.id,
                area: row.area,
            },
        ],
    });

    if (actionable) return gateSentence(actionable);

    const waiting = rows.find((row) => row.state === 'waiting' && row.meta.actor);
    if (waiting) {
        const sent = waiting.meta.sentAt ? ` — sent ${whenText(waiting.meta.sentAt, now)}` : '';
        return {
            sentence: `Waiting on ${waiting.meta.actor} to sign the ${waiting.label.toLowerCase()}${sent}.`,
            actions: [
                { id: 'resend', label: 'Resend link', kind: 'primary', intent: 'resend-link', rowId: waiting.id, area: waiting.area },
                { id: 'copy-link', label: 'Copy link', kind: 'ghost', intent: 'copy-link', rowId: waiting.id, area: waiting.area },
            ],
        };
    }

    if (unmet.length > 0) return gateSentence(unmet[0]);

    /**
     * THE RAIL IS CLEAN AND THE SERVER STILL SAYS NO — AND THIS IS WHERE THE BAND USED TO LIE.
     *
     * Everything below offers `Activate contract`, so this is the last point at which the header
     * and the pinned footer can come apart. They did: the footer asked `activationVerdict` and drew
     * a dead button under the server's sentence, while this branch never asked at all and put
     * "Every gate is met. This contract is ready to activate." above a live one (contract 309, whose
     * driver profile the server was refusing over). The verdict is a shared function precisely so
     * neither surface can hold an opinion of its own — see `activationVerdict` for why the strictness
     * only ever travels in this direction.
     *
     * NO ACTION AT ALL, rather than a disabled one: the row the reason belongs to is not necessarily
     * on this rail (most refusal arms are not gates), so there is nowhere honest to send the admin
     * from here. The sentence is the server's, and the footer says the same thing at the same time.
     */
    const verdict = activationVerdict(gate);
    if (verdict.blocked) return { sentence: verdict.reason, actions: [] };

    const yourMoveRow = rows.find((row) => row.state === 'your-move');
    if (yourMoveRow) {
        return {
            sentence: `Every gate is met. ${yourMoveRow.label} is still outstanding, and it does not block activation.`,
            actions: [
                { id: 'activate', label: 'Activate contract', kind: 'primary', intent: 'activate' },
            ],
        };
    }

    return {
        sentence: 'Every gate is met. This contract is ready to activate.',
        actions: [{ id: 'activate', label: 'Activate contract', kind: 'primary', intent: 'activate' }],
    };
}

/**
 * One snapshot in, the whole page out.
 *
 * Nothing here reads a clock, a query cache or the DOM: `now` is an input, so the same snapshot
 * always produces the same page — which is what makes the elapsed counters, the seal timer and
 * the narrator testable at all.
 */
export function computeSetupState(snapshot: SetupSnapshot): SetupState {
    const now = asDate(snapshot.now) ?? new Date();

    // THE PAYLOAD'S WORDING IS READ BEFORE THE REFUSALS ARE LANDED, NOT AFTER. `sign_contract`
    // refuses in CODES [20260804010000], and a code shown to an admin is a raw refusal on three
    // surfaces at once — the row note, the narrator and the footer. `landRefusals` turns each code
    // into the sentence the payload already carries for it, so it has to be handed that map here.
    const codes = blockerCodes(snapshot);
    const blockers = spokenBlockers(snapshot);
    const messages = new Map(blockers.map((blocker) => [blocker.code, blocker.message]));

    const refusals = landRefusals(snapshot.refusals ?? [], messages);
    const refusalByRow = new Map(
        refusals.filter((refusal) => !!refusal.rowId).map((refusal) => [refusal.rowId as ManifestRowId, refusal.message]),
    );

    const gates: ConditionalGates = {
        loanPresent: insuranceLoanPresent(snapshot),
        depositPresent: depositPresent(snapshot),
        partnerScheduleRequired: partnerScheduleRequired(snapshot),
    };

    const rows: ManifestRow[] = ROW_DEFS.map((def) => {
        const cls = rowClassOf(def, gates);
        const code = codesForRow(codes, def.id)[0];
        const serverMessage = code ? messages.get(code) : undefined;

        let facts = rowFacts(def, snapshot, now);

        // A GATE ROW'S done-NESS IS THE SERVER'S. Its voice is still the client's.
        if (cls === 'gate') {
            if (codes) {
                facts = reconcileGateRow(facts, code, serverMessage);
            } else if (def.id === 'ins-loan-agreement') {
                // THE ONE GATE THE CLIENT NEVER HAD [D-B14]. This row's derivation is a RECORD
                // derivation — settled whether or not the thing is on file — so against a server
                // that cannot answer it would read `done` on an unsigned agreement. The fallback is
                // the same seal test `sign_contract` applies, and nothing else.
                const sealed = slotsOf(snapshot, 'INSURANCE_LOAN').some((slot) =>
                    isSealedForActivation(slot),
                );
                if (!sealed && facts.state === 'done') {
                    facts = {
                        ...facts,
                        state: 'your-move',
                        // "when the premium is financed" is false of a year the driver paid for
                        // [D-B45], and D-B14 refuses the activation over it just the same.
                        note: facts.note ?? requiredWhenRecorded(insuranceInstrumentOf(snapshot)),
                    };
                }
            } else if (def.id === 'dep-agreement') {
                // THE SAME FALLBACK, ONE DEPOSIT AT A TIME [D-B15]. `depositsSealed` is the server's
                // own predicate — the legacy `signed_at` + stored URL pair, counted against the
                // DEPOSITS rather than against the documents — so a contract that papered one of
                // its two payments stays a gate here exactly as it does there.
                if (!depositsSealed(snapshot) && facts.state === 'done') {
                    facts = { ...facts, state: 'your-move', note: facts.note ?? REQUIRED_ONCE_DEPOSITED };
                }
            } else if (def.id === 'agr-partner') {
                // THE SAME FALLBACK, AND TODAY IT IS A BELT OVER BRACES RATHER THAN A SECOND
                // READING [D-B48] — said plainly, because a guard whose comment overstates it is
                // how the next reader comes to trust the wrong half of a pair.
                //
                // THE TWO ARMS ABOVE ARE LOAD-BEARING BECAUSE THEIR ROWS ARE RECORD-SHAPED:
                // `ins-loan-agreement` and `dep-agreement` return `done` whether or not the thing
                // is on file, so without this demotion a server that cannot answer would draw a
                // green tick over an unsigned instrument. `partnerScheduleRow` is CEREMONY-shaped
                // instead — it is modelled on `hpaRow`, and its only `done` already sits behind
                // this very seal test — so the condition below cannot currently be true, exactly as
                // it cannot for `agr-hpa`, which is why that row has no arm here at all.
                //
                // IT IS KEPT ANYWAY, AND FOR ONE REASON: the invariant it states — no gate row
                // reads done without the legacy `signed_at` + stored URL pair `sign_contract`
                // itself consumes — is a property of the PAGE, not of one row's current shape, and
                // the row most likely to be rewritten record-shaped is a conditional one. The cost
                // is a branch never taken; the failure it forecloses is a green Activate the
                // server then refuses, which is the whole defect this module exists to prevent.
                if (!isSealedForActivation(slotOf(snapshot, 'PARTNER_MANAGEMENT')) && facts.state === 'done') {
                    facts = { ...facts, state: 'your-move', note: facts.note ?? REQUIRED_ON_A_PARTNER_VEHICLE };
                }
            }
        } else if (def.id === 'dep-agreement' || def.id === 'ins-loan-agreement') {
            // `cls !== 'gate'` ON ONE OF THESE TWO IDS IS THE SUBJECT-LESS CASE — no deposit, no
            // loan — AND THE TWO ROWS NO LONGER READ THE SAME WAY INSIDE IT [D-B41].
            //
            // THE LOAN'S PAPER IS OWED BECAUSE ITS SUBJECT MAY BE. A premium may yet be recorded on
            // this contract, and a recorded one is refused until the document over it is sealed
            // [D-B14] — whichever of the two instruments it is [D-B45] — a chain the admin is
            // already walking, so the row stays counted, stays blue, and its note names the ORDER
            // the chain is done in rather than a condition for caring about it (2026-08-04).
            //
            // THE DEPOSIT'S IS NOT OWED AT ALL. `sign_contract` no longer refuses a contract that
            // took no deposit, so nothing follows from the absence and the note says only what
            // would land if a payment arrived. `rowOptionalOf` greys the row; this line words it.
            const subjectLessNote =
                def.id === 'dep-agreement' ? REQUIRED_WITH_THE_DEPOSIT : REQUIRED_WITH_THE_LOAN;
            facts = { ...facts, note: facts.note ?? subjectLessNote };
        }

        const draft = snapshot.drafts?.[def.id];
        const meta: RowMeta = { ...(facts.meta ?? {}) };
        if (draft) meta.draft = draft;

        // A REFUSAL IS DROPPED THE MOMENT ITS ROW IS SETTLED. The sentence describes the state at
        // the time of the attempt; keeping it on a row that has since been filled in would have
        // the page arguing with itself.
        //
        // EXCEPT ON A RECORD ROW, WHOSE done IS NOT AN ANSWER TO THAT QUESTION. `record()` returns
        // `done` by construction — settled whether or not the thing is on file — so a record row's
        // state cannot tell staleness from the ordinary case, and reading it as staleness threw
        // away every refusal the server files against one (`INSURANCE_PREMIUM_MISSING` and
        // `INSURANCE_DISCOUNT_MISSING` on `ins-premium`, `DRIVER_REPAYMENT_TAG_MISSING` on
        // `drv-file`, the loan arms on `ins-loan`) — the refused tap read as a no-op.
        const refusal =
            cls !== 'record' && facts.state === 'done' ? null : refusalByRow.get(def.id) ?? null;

        const note = refusal
            ? refusal
            : draft
                ? `An unsaved draft of ${draft} exists on this row. It is not counted as progress.`
                // THE SERVER'S SENTENCE IS THE LAST FALLBACK, NEVER AN OVERRIDE. Where a row's own
                // derivation has something to say it says it — the client's copy explains the
                // CONSEQUENCE ("the agreement cannot be sent without this"), which the server's
                // imperative does not. Where it has nothing, a code that reached this row would
                // otherwise be a refusal the admin is never shown.
                : facts.note ?? serverMessage ?? null;

        return {
            id: def.id,
            area: def.area,
            label: labelOf(def, snapshot),
            cls,
            optional: rowOptionalOf(def, gates),
            state: facts.state,
            detail: facts.detail,
            note,
            meta,
            jump: { area: def.area, block: def.block, ...(def.focus ? { focus: def.focus } : {}) },
            refusal,
        };
    });

    const byId = new Map(rows.map((row) => [row.id, row]));

    const areaGroups: AreaGroup[] = SETUP_AREAS.map((area, index) => {
        const groupRows = rows.filter((row) => row.area === area.key);
        const counted = groupRows.filter(isCountedRow);
        const doneCount = counted.filter(isRowSettled).length;
        // SATISFIED IS A WIDER QUESTION THAN THE COUNTER, and deliberately still state-based: a
        // group collapses to its receipt once nothing in it is OUTSTANDING, which a record that is
        // not on file never is. Reading `isRowSettled` here would hold the insurance group open
        // forever on the common contract — no premium, no discount, no loan — which is the rule
        // this page was rebuilt to end.
        const satisfied = groupRows.every((row) => row.state === 'done');
        const section = sectionOf(snapshot, AREA_SECTIONS[area.key]);
        return {
            area: area.key,
            label: area.label,
            index: index + 1,
            rows: groupRows,
            doneCount,
            requiredCount: counted.length,
            satisfied,
            receipt: satisfied ? receiptFor(area.key, snapshot) : null,
            serverCounts: section
                ? { done: section.required_done, total: section.required_total }
                : null,
        };
    });

    const gateRows = rows.filter((row) => row.cls === 'gate');
    /**
     * THE UNMET COUNT IS THE PAYLOAD'S GATE-BOUND BLOCKER COUNT — a subset of the blocker list, not
     * the whole of it.
     *
     * Over the codes that DO reach a gate row it is an identity rather than a coincidence: a gate
     * row is not-done exactly when its code is in the list, and no two of the codes that reach one
     * gate row can be present at once — the server emits them through IF/ELSIF pairs (`_MISSING`
     * against `_AWAITING_SEAL`) or behind each other's suppression (`INSURANCE_NOT_LINKED` against
     * `INSURANCE_POLICY_INCOMPLETE`).
     *
     * IT IS NOT THE LENGTH OF `blockers`. Most of the payload's arms land on a record or render row
     * and are excluded here by class — `INSURANCE_PREMIUM_MISSING` and `INSURANCE_DISCOUNT_MISSING`
     * (both on `ins-premium`), `DRIVER_REPAYMENT_TAG_MISSING`, the terms arms, and `DEPOSIT_MISSING`
     * from a server that predates [D-B41] — so the rail can read zero unmet while the server is
     * still refusing. That is why `canActivate` and not this number is what the activation control
     * answers to, and why `blockedNote` names the server's reason when the two disagree.
     */
    const unmet = gateRows.filter((row) => row.state !== 'done');
    const sealPending = sealPendingFor(snapshot, now, codes);
    const serverStatus = snapshot.serverStatus ?? null;
    const activated = (serverStatus?.contract_status ?? snapshot.status) !== 'DRAFT';

    const gate: ActivationGate = {
        // `can_activate` IS THE ANSWER WHERE THERE IS ONE. The client's conjunction below is what a
        // page that has not heard back yet falls to — never a second authority beside it.
        ready: serverStatus
            ? serverStatus.can_activate === true
            : unmet.length === 0 && !sealPending && !activated,
        canActivate: serverStatus ? serverStatus.can_activate === true : null,
        blockers,
        unmetCount: unmet.length,
        unmet,
        activated,
        sealPending,
        // THE SAME RULE THE ROWS ARE FILTERED BY, and it has to be the same or the panel and the
        // row disagree: a record row is `done` by construction, so its `done` cannot mean the
        // refusal has gone stale — only a row that DERIVES done settles one.
        refusals: refusals.filter((refusal) => {
            if (!refusal.rowId) return true;
            const row = byId.get(refusal.rowId);
            return row?.cls === 'record' || row?.state !== 'done';
        }),
    };

    const progress: SetupProgress = {
        areasComplete: areaGroups.filter((group) => group.satisfied).length,
        areaCount: areaGroups.length,
        needYou: rows.filter((row) => row.state === 'your-move').length,
        waitingOnOthers: rows.filter((row) => row.state === 'waiting').length,
        outstanding: rows.filter((row) => row.state !== 'done').length,
    };

    return { rows, areaGroups, progress, narrator: narratorFor(snapshot, rows, gate, now), gate };
}

// ─── Where a route-less entry lands ──────────────────────────────────────────

const LEAD_DAYS = new Map(ROW_DEFS.map((def) => [def.id, def.leadDays]));
const AREA_ORDER = new Map(SETUP_AREAS.map((area, index) => [area.key, index]));

/**
 * The area an admin who arrived without one should be put in front of.
 *
 * LANDING CARRIES URGENCY SO ORDERING DOES NOT HAVE TO. The rail keeps the owner's fixed order
 * for spatial memory; what moves is where the page opens. The rule is the longest OUTSTANDING
 * EXTERNAL LEAD TIME: among the rows the admin can act on today, start the one whose clock runs
 * on somebody else's calendar — the DVLA registration, the roadworthiness test, the policy —
 * because that is the wait that has not begun yet. Rows already `waiting` are excluded: their
 * clock is running, and there is nothing to start.
 *
 * With nothing to start, the page opens on whatever is still in flight, and failing that on the
 * first area — never on a random one, so a settled contract always opens the same way.
 *
 * TIES ARE BROKEN BY THE RAIL'S OWN ORDER, and deliberately so: two rows that cost the same wait
 * resolve to whichever the admin would reach first walking down the column, which is the driver
 * since the reorder. The tie-break follows `SETUP_AREAS` and has nothing of its own to maintain.
 */
export function resolveLandingArea(state: SetupState): SetupArea {
    const rank = (row: ManifestRow) => LEAD_DAYS.get(row.id) ?? 0;
    const byLead = (a: ManifestRow, b: ManifestRow) =>
        rank(b) - rank(a) || (AREA_ORDER.get(a.area) ?? 0) - (AREA_ORDER.get(b.area) ?? 0);

    const startable = state.rows.filter((row) => row.state === 'your-move').sort(byLead);
    if (startable.length > 0) return startable[0].area;

    const inFlight = state.rows.filter((row) => row.state === 'waiting' || row.state === 'locked').sort(byLead);
    if (inFlight.length > 0) return inFlight[0].area;

    return SETUP_AREAS[0].key;
}

/** The area label, for callers that hold a key. */
export function areaLabel(area: SetupArea): string {
    return AREA_LABELS[area];
}
